import { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import { Link } from 'react-router-dom';
import { Search, ShoppingCart, Plus, Minus, Trash2, X, FileText, DollarSign, Lock, Unlock, HelpCircle, Info, Ticket, Package, Printer, Wallet, ArrowDownToLine, Shield, List } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { isCajeroUser, canManageVendedorCodigos } from '../utils/userRoles';
import { resolveTotalVentasReales } from '../utils/ventasReales';
import { useIdleTimeout } from '../hooks/useIdleTimeout';
import { useTableSort } from '../hooks/useTableSort';
import SortableTh from '../components/SortableTh';
import DenominacionEfectivo from '../components/DenominacionEfectivo';
import {
    createEmptyDesglose,
    compactDesglose,
    parseMontoClp,
    validateDesgloseVsMonto,
    formatDesgloseDetalle,
    desgloseLineas,
} from '../utils/denominacionesClp';
import { formatFechaHoraChile } from '../utils/fechasChile';
import { printComprobanteMovimientoEfectivo } from '../utils/printMovimientoEfectivo';
import { productImageSrc, retryProductImageOnce } from '../utils/productImage';
import { applyAutoPacks, countProductUnits, setProductUnits, snapshotProduct } from '../utils/autoPackPromos';
import { codigoPersonal, normalizeStaffCode } from '../utils/personalCodigo';

const ROLE_ADMIN = 4;
const ROLE_PREVENTA = 1;
const ROLE_VENDEDOR = 2;

/** Cobro informado en ticket cuando el cliente no trae envase retornable (solo comprobante impreso). */
const ENVASE_TICKET_MONTO_CLP = 500;
/** Ticket envase bidón agua 20 L. */
const ENVASE_BIDON_MONTO_CLP = 3000;
/** Cash advance (giro) charged on card and handed to the customer. */
const GIRO_TARJETA_MAX_CLP = 20000;
const GIRO_TARJETA_QUICK_AMOUNTS = [1000, 2000, 3000, 5000, 10000, 20000];
/** Bills the customer can hand over. Each tap adds that bill to cash received. */
const BILLETES_RECIBIDOS_CLP = [1000, 2000, 5000, 10000, 20000];

/** Max gap between keystrokes to treat input as barcode scanner (ms). */
/** Max gap between keystrokes to treat input as barcode scanner (ms). Some pistols are slower. */
const SCANNER_KEY_GAP_MS = 180;

const IDLE_TIMEOUT_MS = 60 * 60 * 1000;
const IDLE_WARN_MS = 60 * 1000;

/** "huevos" and "huevo" match the same offer names. */
function foldOfferSearch(value) {
    return String(value || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/s\b/g, '');
}

export default function POSPage() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const searchInputRef = useRef(null);
    const hasAutoShownOpenCajaRef = useRef(false);
    /** True while a modal owns the keyboard; the scan field must not steal focus. */
    const scanFocusPausedRef = useRef(false);

    // Core State
    const [products, setProducts] = useState([]);
    const [cart, setCart] = useState([]);
    /** Last completed sale that can still be voided if the customer did not pay. */
    const [ultimaVentaAnulable, setUltimaVentaAnulable] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    
    // Toast notification for barcode not found
    const [toast, setToast] = useState({ show: false, message: '', type: 'error' });

    // POS Feature State
    const [cajaSession, setCajaSession] = useState(null); // { isOpen: bool, session: {} }
    const [availableCajas, setAvailableCajas] = useState([]);
    const [preventas, setPreventas] = useState([]);
    const { sorted: sortedPreventas, thProps: preventaThProps } = useTableSort(preventas, {
        defaultKey: 'id',
        defaultDir: 'desc',
        getters: {
            cantidad_productos: (p) => Number(p.cantidad_productos) || 0,
            sub_total: (p) => Number(p.sub_total) || 0,
        },
    });
    const [activePreventaId, setActivePreventaId] = useState(null);
    const [cajaStatusLoading, setCajaStatusLoading] = useState(false); // Para evitar mostrar modal mientras se carga

    // Modals
    const [showPreventaModal, setShowPreventaModal] = useState(false); // List of pending preventas
    const [promotions, setPromotions] = useState([]);
    const [promotionsInSearch, setPromotionsInSearch] = useState([]);
    const [promoSelectionModal, setPromoSelectionModal] = useState(null);
    const [showOpenCajaModal, setShowOpenCajaModal] = useState(false);
    const [showCloseCajaModal, setShowCloseCajaModal] = useState(false);
    const [showPesoModal, setShowPesoModal] = useState(false);
    const [pesoProductToAdd, setPesoProductToAdd] = useState(null);
    const [pesoGramsInput, setPesoGramsInput] = useState('');
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    /** Montos como texto para aceptar formato CLP (3500 o 3.500); se parsean con parseMontoClp. */
    const [paymentData, setPaymentData] = useState({
        efectivo: '',
        tarjeta_debito: '',
        tarjeta_credito: '',
    });
    /** Vuelto entregado desde caja (p. ej. cargo alto en tarjeta y efectivo al cliente); cuadra egreso en caja_efectivo_ajuste */
    const [vueltoEfectivo, setVueltoEfectivo] = useState('');
    const [openCajaData, setOpenCajaData] = useState({ caja_id: '', monto_inicial: '' });
    const [openCajaDesglose, setOpenCajaDesglose] = useState(() => createEmptyDesglose());
    const [closeCajaData, setCloseCajaData] = useState({ efectivo_cierre: '' });
    const [closeCajaDesglose, setCloseCajaDesglose] = useState(() => createEmptyDesglose());
    const [closeCajaForzar, setCloseCajaForzar] = useState(false);
    const [cierreResumen, setCierreResumen] = useState(null);
    const [showPagoCuentaModal, setShowPagoCuentaModal] = useState(false);
    const [pagoCuentaModo, setPagoCuentaModo] = useState('efectivo'); // 'efectivo' | 'tarjeta_externa'
    const [pagoCuentaMonto, setPagoCuentaMonto] = useState('');
    const [pagoCuentaLoading, setPagoCuentaLoading] = useState(false);
    const [showMovimientoEfectivoModal, setShowMovimientoEfectivoModal] = useState(false);
    /** 'inyeccion_efectivo' | 'retiro_efectivo_seguridad' */
    const [movimientoEfectivoTipo, setMovimientoEfectivoTipo] = useState('inyeccion_efectivo');
    const [movimientoEfectivoMonto, setMovimientoEfectivoMonto] = useState('');
    const [movimientoEfectivoDesglose, setMovimientoEfectivoDesglose] = useState(() => createEmptyDesglose());
    /** Hint when opening injection from payment modal (insufficient cash for change). */
    const [movimientoEfectivoSugerido, setMovimientoEfectivoSugerido] = useState(null);
    const [movimientoEfectivoLoading, setMovimientoEfectivoLoading] = useState(false);
    const [showMovimientosListModal, setShowMovimientosListModal] = useState(false);
    const [movimientosEfectivo, setMovimientosEfectivo] = useState([]);
    const [movimientosEfectivoLoading, setMovimientosEfectivoLoading] = useState(false);

    /** Solo en flujo «Completar venta» (modal de pago): cargo envase en boleta (0 | 500 | 3000). */
    const [boletaCargoEnvaseMonto, setBoletaCargoEnvaseMonto] = useState(0);
    const [boletaDescuentoDevolucionEnvase, setBoletaDescuentoDevolucionEnvase] = useState(false);

    // Vendedor asignado por escaneo de código de barras (VND + id, ej. VND42)
    const [selectedVendedor, setSelectedVendedor] = useState(null);
    // Modal para preguntar vendedor/cajero al crear preventa (solo escaneo)
    const [showVendedorPreventaModal, setShowVendedorPreventaModal] = useState(false);
    const [vendedorPreventaInput, setVendedorPreventaInput] = useState('');
    const [vendedorPreventaResolved, setVendedorPreventaResolved] = useState(null); // { id, name }
    const vendedorPreventaInputRef = useRef(null);
    const vendedorScanBufferRef = useRef('');
    const vendedorScanLastKeyRef = useRef(0);
    const vendedorScanIdleTimerRef = useRef(null);
    /** While a ticket print dialog is open, ignore scanner/keyboard on the POS search field. */
    const [isPrinting, setIsPrinting] = useState(false);
    const printLockTimerRef = useRef(null);

    const roleId = Number(user?.role ?? user?.tipo_usuario_id);
    const isPreventaPrevendedor = roleId === ROLE_PREVENTA;
    const isVendedorRole = roleId === ROLE_VENDEDOR;
    const isAdmin = roleId === ROLE_ADMIN;
    const isCajero = isCajeroUser(user);
    /** Cajero (no admin): no mostrar efectivo esperado / disponible del sistema. */
    const ocultarEfectivoSistema = isCajero && !isAdmin;
    /** Solo Preventa y Admin generan preventas. El cajero cobra venta directa o carga PRV. */
    const canCreatePreventa = (isPreventaPrevendedor || isAdmin) && !isCajero;
    const requiereCajaOperativa = isCajero || isAdmin;
    const canCompleteSale = requiereCajaOperativa;
    const canCompleteSaleWithLoadedPreventa = canCompleteSale || (isVendedorRole && activePreventaId);
    /** Cajero/admin con turno abierto pero caja o sucursal inactiva: no cobrar hasta corregir en administración. */
    const cajeroVenBloqueadoPorEstadoLocal =
        requiereCajaOperativa &&
        Boolean(cajaSession?.isOpen) &&
        cajaSession?.sales_blocked === true;
    /** Cajero o admin puede cobrar solo con caja abierta y operativa. */
    const puedeEjecutarCobro =
        requiereCajaOperativa &&
        Boolean(cajaSession?.isOpen) &&
        cajaSession?.sales_blocked !== true;
    /** Alias for cajero-specific UI (vuelto, envases) — same rule as admin now. */
    const cajeroPuedeEjecutarCobro = puedeEjecutarCobro;
    /** Total con envases en boleta / toggles POS que requieren caja válida. */
    const opcionesSesionCajaVentasActivas =
        canCompleteSale &&
        Boolean(cajaSession?.isOpen) &&
        cajaSession?.sales_blocked !== true;
    const canLoadPreventaInPos = !isPreventaPrevendedor;
    const canVendedorCodigos = canManageVendedorCodigos(user);
    /** Preventa/Vendedor pueden escanear VND para asignación; admin/jefe igual */
    const canScanVendedorBarcode = canVendedorCodigos || isPreventaPrevendedor || isVendedorRole || canCompleteSale;
    /** Cajero/Admin: VND opcional (sin escaneo se atribuye al cajero). Vendedor: obligatorio. */
    const saleRequiresVnd = isVendedorRole;
    const showVendedorAssignment = canCompleteSaleWithLoadedPreventa;

    useEffect(() => {
        if (user?.id != null) {
            fetchAvailableCajas();
        }
        if (user?.id != null) {
            fetchCajaStatus();
        }
    }, [user?.id]);

    useEffect(() => {
        fetchPromotions();
    }, []);

    useEffect(() => {
        setCart((prev) => (prev.length ? applyAutoPacks(prev, promotions).cart : prev));
    }, [promotions]);

    useEffect(() => {
        const term = searchTerm.trim();
        const folded = foldOfferSearch(term);
        const skip = folded.length < 3
            || /^\d{6,14}$/.test(term)
            || /^(prv|vnd|caj|env)/i.test(term);
        if (skip || !promotions.length) {
            setPromotionsInSearch([]);
            return;
        }
        setPromotionsInSearch(promotions.filter((promo) => {
            const blob = foldOfferSearch([promo.nombre, promo.descripcion].filter(Boolean).join(' '));
            return blob.includes(folded);
        }));
    }, [searchTerm, promotions]);

    useEffect(() => {
        if (isCajero && searchParams.get('openCaja') === '1' && !cajaSession?.isOpen) {
            setShowOpenCajaModal(true);
            setSearchParams({}, { replace: true });
        }
    }, [isCajero, searchParams, cajaSession?.isOpen, setSearchParams]);

    // Mostrar modal de abrir caja automáticamente solo para CAJEROS que no tienen caja abierta
    useEffect(() => {
        if (isCajero && !cajaStatusLoading && !cajaSession?.isOpen && availableCajas.length > 0 && !hasAutoShownOpenCajaRef.current) {
            hasAutoShownOpenCajaRef.current = true;
            setShowOpenCajaModal(true);
        }
        if (cajaSession?.isOpen) {
            hasAutoShownOpenCajaRef.current = false;
        }
    }, [isCajero, cajaStatusLoading, cajaSession?.isOpen, availableCajas.length]);

    // Este useEffect ya no es necesario porque el render condicional maneja la pantalla completa
    // El render condicional `if (cajeroMustOpenCaja)` muestra la pantalla completa cuando el cajero no tiene caja abierta
    // Solo mantener el useEffect para casos especiales (como cuando se viene de otra página con parámetro openCaja=1)

    useEffect(() => {
        if (cart.length === 0) {
            setBoletaCargoEnvaseMonto(0);
            setBoletaDescuentoDevolucionEnvase(false);
        }
    }, [cart.length]);

    useEffect(() => {
        if (!cajaSession?.isOpen) {
            setBoletaCargoEnvaseMonto(0);
            setBoletaDescuentoDevolucionEnvase(false);
        }
    }, [cajaSession?.isOpen]);

    // Debounced search - same as ProductsPage
    // Also detects preventa barcode (PRV), vendedor barcode (VND), and products
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            const trimmed = searchTerm.trim();
            if (!trimmed) {
                setProducts([]);
                return;
            }
            const searchUpper = searchTerm.toUpperCase().trim();
            // Check if scanned a preventa barcode (PRV00000123 format)
            if (searchUpper.startsWith('PRV') && searchUpper.length >= 4) {
                const preventaId = parseInt(searchUpper.substring(3), 10);
                if (!isNaN(preventaId) && preventaId > 0) {
                    if (isPreventaPrevendedor) {
                        showToast('Su rol solo genera preventas. El código PRV lo escanea caja para cobrar.', 'error');
                        setSearchTerm('');
                        setProducts([]);
                        setTimeout(() => searchInputRef.current?.focus(), 50);
                        return;
                    }
                    console.log('Preventa barcode detected:', preventaId);
                    loadPreventa(preventaId);
                    setSearchTerm('');
                    return;
                }
            }
            if (searchUpper.startsWith('VND') && searchUpper.length >= 4 && !canScanVendedorBarcode) {
                showToast('Los códigos de vendedor (VND) no están habilitados para su perfil.', 'error');
                setSearchTerm('');
                setProducts([]);
                setTimeout(() => searchInputRef.current?.focus(), 50);
                return;
            }
            if (searchUpper.startsWith('CAJ') && searchUpper.length >= 4 && !canScanVendedorBarcode) {
                showToast('Los códigos de cajero (CAJ) no están habilitados para su perfil.', 'error');
                setSearchTerm('');
                setProducts([]);
                setTimeout(() => searchInputRef.current?.focus(), 50);
                return;
            }
            if (
                canScanVendedorBarcode &&
                ((searchUpper.startsWith('VND') && searchUpper.length >= 4) ||
                    (searchUpper.startsWith('CAJ') && searchUpper.length >= 4))
            ) {
                const code = searchTerm.trim();
                fetch(`${API_URL}/api/users/vendedor-by-barcode/${encodeURIComponent(code)}`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                })
                    .then(res => {
                        if (res.ok) return res.json();
                        throw new Error(res.status === 404 ? 'Código no encontrado' : 'Código no válido');
                    })
                    .then(data => {
                        setSelectedVendedor({
                            id: data.id,
                            name: data.name,
                            codigo: normalizeStaffCode(code),
                        });
                        showToast(
                            searchUpper.startsWith('CAJ')
                                ? `Cajero asignado: ${data.name}`
                                : `Vendedor asignado: ${data.name}`,
                            'success'
                        );
                        setSearchTerm('');
                        setProducts([]);
                        setTimeout(() => searchInputRef.current?.focus(), 50);
                    })
                    .catch(() => {
                        showToast('Código de personal no válido (VND… / CAJ…)', 'error');
                        setSearchTerm('');
                        setTimeout(() => searchInputRef.current?.focus(), 50);
                    });
                return;
            }
            // Ticket envase — ENV + dígitos (trajo envase de vuelta)
            const envCodigoUpper = searchUpper.replace(/\s+/g, '');
            if (/^ENV\d{10,}$/.test(envCodigoUpper)) {
                if (!isCajero || !cajeroPuedeEjecutarCobro) {
                    showToast(
                        !isCajero
                            ? 'Solo personal de caja puede procesar la devolución de envase (escanee ENV…).'
                            : (cajaSession?.sales_blocked_reason ||
                              'Su turno no puede registrar ENV: abra una caja operativa con sucursal activa.'),
                        'error'
                    );
                    setSearchTerm('');
                    setProducts([]);
                    setTimeout(() => searchInputRef.current?.focus(), 50);
                    return;
                }
                setLoading(true);
                fetch(`${API_URL}/api/cajas/envase-devolver`, {
                    method: 'POST',
                    headers: { ...authHeader(), 'Content-Type': 'application/json' },
                    body: JSON.stringify({ codigo: envCodigoUpper }),
                })
                    .then(async res => {
                        const data = await res.json().catch(() => ({}));
                        if (res.status === 401) {
                            logout();
                            throw new Error('session');
                        }
                        if (!res.ok) throw new Error(data.error || 'No se pudo registrar la devolución');
                        return data;
                    })
                    .then(data => {
                        showToast(`Devolución envase ${formatCurrency(data.monto)} registrada. Entregue efectivo al cliente.`, 'success');
                        setSearchTerm('');
                        setProducts([]);
                        setTimeout(() => searchInputRef.current?.focus(), 50);
                    })
                    .catch(err => {
                        if (err.message !== 'session') {
                            showToast(err.message || 'Error al procesar código ENV', 'error');
                        }
                        setSearchTerm('');
                        setProducts([]);
                        setTimeout(() => searchInputRef.current?.focus(), 50);
                    })
                    .finally(() => setLoading(false));
                return;
            }
            // Enable auto-add on exact barcode match for scanner workflow
            fetchProducts(searchTerm, true);
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm, canScanVendedorBarcode, isPreventaPrevendedor, isCajero]);

    // Validate VND/CAJ barcode when scanned in preventa modal (scan-only field)
    useEffect(() => {
        if (!showVendedorPreventaModal || !vendedorPreventaInput.trim()) {
            setVendedorPreventaResolved(null);
            return;
        }
        const upper = vendedorPreventaInput.toUpperCase().trim();
        const isVnd = upper.startsWith('VND') && upper.length >= 4;
        const isCaj = upper.startsWith('CAJ') && upper.length >= 4;
        if (!isVnd && !isCaj) {
            setVendedorPreventaResolved(null);
            return;
        }
        const timer = setTimeout(() => {
            fetch(`${API_URL}/api/users/vendedor-by-barcode/${encodeURIComponent(vendedorPreventaInput.trim())}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            })
                .then(res => res.ok ? res.json() : Promise.reject())
                .then(data => setVendedorPreventaResolved({
                    id: data.id,
                    name: data.name,
                    codigo: normalizeStaffCode(vendedorPreventaInput),
                }))
                .catch(() => setVendedorPreventaResolved(null));
        }, 200);
        return () => clearTimeout(timer);
    }, [showVendedorPreventaModal, vendedorPreventaInput]);

    // Helper to show toast notification
    const showToast = (message, type = 'error') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'error' }), 3000);
    };

    const beginPrintLock = () => {
        scanFocusPausedRef.current = true;
        searchInputRef.current?.blur();
        vendedorPreventaInputRef.current?.blur();
        setIsPrinting(true);
        if (printLockTimerRef.current) clearTimeout(printLockTimerRef.current);
        printLockTimerRef.current = setTimeout(() => setIsPrinting(false), 5000);
    };

    useEffect(() => {
        if (!isPrinting) return;
        const swallowKeys = (e) => {
            e.preventDefault();
            e.stopPropagation();
        };
        window.addEventListener('keydown', swallowKeys, true);
        return () => window.removeEventListener('keydown', swallowKeys, true);
    }, [isPrinting]);

    useEffect(() => {
        return () => {
            if (printLockTimerRef.current) clearTimeout(printLockTimerRef.current);
        };
    }, []);

    useIdleTimeout({
        enabled: requiereCajaOperativa && Boolean(user) && !showPaymentModal && !showPesoModal,
        timeoutMs: IDLE_TIMEOUT_MS,
        warnMs: IDLE_WARN_MS,
        onWarn: () => showToast('Su sesión expirará por inactividad en 1 minuto.', 'error'),
        onTimeout: () => {
            logout();
            navigate('/login');
        },
    });

    // Check if string looks like a barcode (numeric, 6-13 digits)
    const looksLikeBarcode = (str) => /^\d{6,14}$/.test(str.trim());

    // Data Fetching
    const fetchProducts = async (search = '', autoAddOnExactMatch = false) => {
        setLoading(true);
        try {
            const sucursalId = cajaSession?.session?.sucursal_id || availableCajas[0]?.sucursal_id || 1;
            const params = new URLSearchParams();
            if (search) params.set('search', search);
            params.set('sucursal_id', sucursalId);
            params.set('pos_search', '1'); // Código exacto; nombre solo productos sin codigo_barra
            params.set('limit', looksLikeBarcode(search.trim()) ? '5' : '50');
            const url = `${API_URL}/api/products?${params.toString()}`;
            console.log('Fetching products from:', url);
            const res = await fetch(url, { headers: authHeader() });
            if (res.ok) {
                const data = await res.json();
                console.log('Products received:', data.length);
                // Sort: products with stock first, products without stock at the end
                const sorted = [...data].sort((a, b) => {
                    const sa = a.stock_actual ?? 0;
                    const sb = b.stock_actual ?? 0;
                    if (sa <= 0 && sb > 0) return 1;
                    if (sa > 0 && sb <= 0) return -1;
                    return (a.nombre || '').localeCompare(b.nombre || '');
                });
                
                const searchTrimmed = search.trim();
                const isBarcodeScan = looksLikeBarcode(searchTrimmed);
                
                // Check for exact barcode match (scanner detection)
                if (autoAddOnExactMatch && searchTrimmed) {
                    const exactMatch = data.find(p => 
                        p.codigo_barra && p.codigo_barra.toLowerCase() === searchTrimmed.toLowerCase()
                    );
                    
                    if (exactMatch) {
                        console.log('Exact barcode match found, auto-adding:', exactMatch.nombre);
                        if (exactMatch.venta_por_peso) {
                            setPesoProductToAdd(exactMatch);
                            setPesoGramsInput('');
                            setShowPesoModal(true);
                        } else {
                            addToCart(exactMatch, true);
                        }
                        setSearchTerm('');
                        setProducts([]);
                        setTimeout(() => searchInputRef.current?.focus(), 50);
                        return;
                    }
                    
                    // If it looks like a barcode scan but no exact match found
                    if (isBarcodeScan) {
                        console.log('Barcode not found:', searchTrimmed);
                        showToast(`Producto no encontrado: ${searchTrimmed}`, 'error');
                        setSearchTerm('');
                        setProducts([]);
                        setTimeout(() => searchInputRef.current?.focus(), 50);
                        return;
                    }
                }
                
                setProducts(sorted);
            } else {
                console.error('API error:', res.status, res.statusText);
                setProducts([]);
            }
        } catch (err) { 
            console.error('Fetch error:', err); 
            setProducts([]);
        }
        finally { setLoading(false); }
    };

    const fetchAvailableCajas = async () => {
        try {
            const url = isCajero
                ? `${API_URL}/api/cajas/available-for-me`
                : `${API_URL}/api/cajas`;
            const res = await fetch(url, { headers: authHeader() });
            if (res.status === 401) { logout(); return; }
            if (res.ok) {
                const data = await res.json();
                setAvailableCajas(Array.isArray(data) ? data : []);
            } else {
                const errBody = await res.json().catch(() => ({}));
                const msg = errBody.error || `No se pudieron cargar las cajas (código ${res.status}).`;
                console.error('Cajas:', res.status, errBody);
                setAvailableCajas([]);
                if (isCajero) showToast(msg, 'error');
            }
        } catch (err) {
            console.error(err);
            setAvailableCajas([]);
            if (isCajero) showToast('Error de red al cargar las cajas.', 'error');
        }
    };

    const fetchPromotions = async () => {
        try {
            const res = await fetch(`${API_URL}/api/promotions`, { headers: authHeader() });
            if (res.status === 401) { logout(); return; }
            const data = await res.json().catch(() => null);
            if (res.ok && Array.isArray(data)) {
                setPromotions(data);
                return;
            }
            setPromotions([]);
            if (!res.ok) {
                showToast(data?.error || 'No se pudieron cargar las promociones.', 'error');
            }
        } catch (err) {
            console.error(err);
            setPromotions([]);
        }
    };

    const fetchCajaStatus = async () => {
        setCajaStatusLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/cajas/status`, {
                headers: authHeader()
            });
            if (res.status === 401) { logout(); return; }
            if (res.ok) {
                const data = await res.json();
                console.log('✅ Caja status fetched:', data);
                console.log('📊 Estado anterior:', cajaSession);
                setCajaSession(data);
                console.log('📊 Estado nuevo será:', data);
                // Si la caja está abierta, asegurarse de que el modal esté cerrado
                if (data.isOpen) {
                    console.log('🔓 Caja abierta, cerrando modal');
                    setShowOpenCajaModal(false);
                } else {
                    console.log('🔒 Caja cerrada');
                }
            } else {
                console.error('❌ Error en respuesta de caja status:', res.status, res.statusText);
                setCajaSession({ isOpen: false });
            }
        } catch (err) { 
            console.error('❌ Error fetching caja status:', err);
            setCajaSession({ isOpen: false });
        } finally {
            setCajaStatusLoading(false);
        }
    };

    const fetchPreventas = async () => {
        try {
            const res = await fetch(`${API_URL}/api/preventas`, {
                headers: authHeader()
            });
            if (res.status === 401) { logout(); return; }
            if (res.ok) setPreventas(await res.json());
        } catch (err) { console.error(err); }
    };

    // Actions
    const handleOpenCaja = async (e) => {
        e.preventDefault();
        const montoInicial = parseMontoClp(openCajaData.monto_inicial) ?? 0;
        if (!openCajaData.caja_id || montoInicial < 0) {
            showToast('Debe seleccionar una caja e ingresar el monto en efectivo.', 'error');
            return;
        }
        const mismatch = validateDesgloseVsMonto(openCajaDesglose, montoInicial);
        if (mismatch) {
            showToast(mismatch, 'error');
            return;
        }
        setCajaStatusLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/cajas/open`, {
                method: 'POST',
                headers: { ...authHeader(), 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    caja_id: openCajaData.caja_id,
                    monto_inicial: montoInicial,
                    desglose: compactDesglose(openCajaDesglose),
                })
            });
            if (res.status === 401) { logout(); return; }
            if (res.ok) {
                const result = await res.json();
                console.log('✅ Caja abierta exitosamente:', result);
                
                setOpenCajaData({ caja_id: '', monto_inicial: '' });
                setOpenCajaDesglose(createEmptyDesglose());
                setShowOpenCajaModal(false);
                
                const newCajaSession = { 
                    isOpen: true, 
                    session: result 
                };
                setCajaSession(newCajaSession);
                setCajaStatusLoading(false);
                
                showToast('Caja abierta correctamente. Ya puede registrar ventas.', 'success');
            } else {
                const errorData = await res.json().catch(() => ({}));
                console.error('❌ Error al abrir caja:', errorData);
                const errMsg = errorData.error || 'Error al abrir la caja';
                if (errMsg.includes('already has an open caja')) {
                    setShowOpenCajaModal(false);
                    fetchCajaStatus();
                    showToast('Ya tiene una caja abierta. Puede continuar operando.', 'success');
                } else {
                    alert(errMsg);
                }
                setCajaStatusLoading(false);
            }
        } catch (err) {
            console.error('❌ Error al abrir caja:', err);
            alert('Error al abrir la caja');
            setCajaStatusLoading(false);
        }
    };

    const handleCloseCaja = async (e) => {
        e.preventDefault();
        const efectivo = parseMontoClp(closeCajaData.efectivo_cierre);
        if (efectivo === null) {
            showToast('Debe ingresar el monto de efectivo (conteo físico en caja).', 'error');
            return;
        }
        const mismatch = validateDesgloseVsMonto(closeCajaDesglose, efectivo);
        if (mismatch) {
            showToast(mismatch, 'error');
            return;
        }

        const esperado = cierreResumen?.efectivo_esperado_en_caja ?? null;
        const diff =
            esperado != null && Number.isFinite(Number(esperado))
                ? efectivo - Number(esperado)
                : null;
        const hayDescuadre = diff != null && Math.abs(diff) > 1;

        if (hayDescuadre && !closeCajaForzar) {
            showToast(
                ocultarEfectivoSistema
                    ? 'El conteo no cuadra con el sistema. Marque "Cerrar con descuadre" o vuelva a contar el efectivo.'
                    : `El conteo difiere del sistema (${formatCurrency(diff)}). Marque "Cerrar con descuadre" o ajuste el monto.`,
                'error'
            );
            return;
        }

        if (closeCajaForzar && hayDescuadre) {
            const ok = window.confirm(
                ocultarEfectivoSistema
                    ? `El efectivo contado (${formatCurrency(efectivo)}) no coincide con el esperado del sistema.\n\n` +
                          '¿Cerrar el turno de todas formas? Quedará registrado en el corte.'
                    : `El efectivo contado (${formatCurrency(efectivo)}) no coincide con el esperado (${formatCurrency(esperado)}).\n\n` +
                          `Diferencia: ${formatCurrency(diff)}\n\n` +
                          '¿Cerrar el turno de todas formas? Quedará registrado en el corte.'
            );
            if (!ok) return;
        }

        const payload = {
            efectivo_cierre: efectivo,
            desglose: compactDesglose(closeCajaDesglose),
            ...(closeCajaForzar && hayDescuadre ? { forzar_cierre: true } : {}),
        };
        try {
            const res = await fetch(`${API_URL}/api/cajas/close`, {
                method: 'POST',
                headers: { ...authHeader(), 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (res.status === 401) { logout(); navigate('/login'); return; }
            if (res.ok) {
                const data = await res.json().catch(() => ({}));
                if (data.corte_turno) {
                    printCorteTurno(data.corte_turno);
                }
                setShowCloseCajaModal(false);
                setCierreResumen(null);
                setCloseCajaData({ efectivo_cierre: '' });
                setCloseCajaDesglose(createEmptyDesglose());
                setCloseCajaForzar(false);
                logout();
                navigate('/login');
            } else {
                const errorData = await res.json().catch(() => ({}));
                const msg = errorData.detalle?.efectivo
                    ? (ocultarEfectivoSistema
                        ? `${errorData.error}\nContado: ${formatCurrency(errorData.detalle.efectivo.ingresado)}${errorData.puede_forzar_cierre ? '\n\nPuede marcar "Cerrar con descuadre" para continuar.' : ''}`
                        : `${errorData.error}\nEsperado: ${formatCurrency(errorData.detalle.efectivo.esperado)}\nContado: ${formatCurrency(errorData.detalle.efectivo.ingresado)}\nDiferencia: ${formatCurrency(errorData.detalle.efectivo.diff ?? 0)}${errorData.puede_forzar_cierre ? '\n\nPuede marcar "Cerrar con descuadre" para continuar.' : ''}`)
                    : (errorData.error || 'No se pudo cerrar la caja');
                alert(msg);
            }
        } catch (err) {
            console.error(err);
            alert('No se pudo cerrar la caja. Por favor intente nuevamente.');
        }
    };

    const openCloseCajaModal = async () => {
        setCloseCajaForzar(false);
        setCloseCajaDesglose(createEmptyDesglose());
        try {
            const res = await fetch(`${API_URL}/api/cajas/cierre-resumen`, { headers: authHeader() });
            if (res.status === 401) { logout(); return; }
            if (res.ok) {
                const data = await res.json();
                setCierreResumen(data);
                if (ocultarEfectivoSistema) {
                    // Cajero must count physically — never prefill with system expected cash.
                    setCloseCajaData({ efectivo_cierre: '' });
                } else {
                    const esperado = Number(data.efectivo_esperado_en_caja);
                    const prefill =
                        Number.isFinite(esperado) && esperado >= 0 ? String(Math.round(esperado)) : '';
                    setCloseCajaData({ efectivo_cierre: prefill });
                }
            } else {
                setCierreResumen(null);
                setCloseCajaData({ efectivo_cierre: '' });
            }
        } catch (err) {
            console.error(err);
            setCierreResumen(null);
            setCloseCajaData({ efectivo_cierre: '' });
        }
        setShowCloseCajaModal(true);
    };

    /** Register utility/account payment (cash in drawer, or external card — audit only). */
    const handlePagoCuenta = async () => {
        const monto = Math.round(Number(String(pagoCuentaMonto).replace(/\./g, '')) || 0);
        if (monto <= 0) {
            showToast('Indique un monto válido para el pago de cuenta.', 'error');
            return;
        }
        const tipo = pagoCuentaModo === 'tarjeta_externa' ? 'pago_cuenta_tarjeta_externa' : 'pago_cuenta_efectivo';
        setPagoCuentaLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/cajas/ajuste-efectivo`, {
                method: 'POST',
                headers: { ...authHeader(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ tipo, monto }),
            });
            if (res.status === 401) {
                logout();
                return;
            }
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                showToast(data.error || 'No se pudo registrar el pago de cuenta.', 'error');
                return;
            }
            const etiqueta = pagoCuentaModo === 'tarjeta_externa' ? 'Pago de cuenta (tarjeta externa)' : 'Pago de cuenta (efectivo)';
            showToast(`${etiqueta} ${formatCurrency(monto)} registrado.`, 'success');
            setShowPagoCuentaModal(false);
            setPagoCuentaMonto('');
            setPagoCuentaModo('efectivo');
            refreshEfectivoCaja();
        } catch (err) {
            console.error(err);
            showToast('Error de red al registrar pago de cuenta.', 'error');
        } finally {
            setPagoCuentaLoading(false);
        }
    };

    /** Cash injection or security withdrawal during open shift. */
    const handleMovimientoEfectivo = async () => {
        const monto = parseMontoClp(movimientoEfectivoMonto) ?? 0;
        if (monto <= 0) {
            showToast('Indique un monto válido.', 'error');
            return;
        }
        const mismatch = validateDesgloseVsMonto(movimientoEfectivoDesglose, monto);
        if (mismatch) {
            showToast(mismatch, 'error');
            return;
        }
        setMovimientoEfectivoLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/cajas/ajuste-efectivo`, {
                method: 'POST',
                headers: { ...authHeader(), 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    tipo: movimientoEfectivoTipo,
                    monto,
                    desglose: compactDesglose(movimientoEfectivoDesglose),
                }),
            });
            if (res.status === 401) {
                logout();
                return;
            }
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                showToast(data.error || 'No se pudo registrar el movimiento.', 'error');
                return;
            }
            const etiqueta =
                movimientoEfectivoTipo === 'inyeccion_efectivo'
                    ? 'Inyección de efectivo'
                    : 'Retiro por seguridad';
            showToast(`${etiqueta} ${formatCurrency(monto)} registrado.`, 'success');
            printComprobanteMovimientoEfectivo({
                tipo: movimientoEfectivoTipo === 'inyeccion_efectivo' ? 'ingreso' : 'egreso',
                concepto: movimientoEfectivoTipo,
                etiqueta,
                monto,
                desglose: compactDesglose(movimientoEfectivoDesglose),
                created_at: new Date().toISOString(),
                responsable: user?.name || 'Cajero',
                caja_nombre:
                    cajaSession?.session?.caja_nombre ||
                    availableCajas.find((c) => c.id == cajaSession?.session?.caja_id)?.nombre ||
                    'POS',
            });
            setShowMovimientoEfectivoModal(false);
            setMovimientoEfectivoMonto('');
            setMovimientoEfectivoDesglose(createEmptyDesglose());
            setMovimientoEfectivoSugerido(null);
            refreshEfectivoCaja();
            fetchMovimientosEfectivo();
        } catch (err) {
            console.error(err);
            showToast('Error de red al registrar movimiento de efectivo.', 'error');
        } finally {
            setMovimientoEfectivoLoading(false);
        }
    };

    const doCreatePreventa = async (vendedorToUse) => {
        if (isCajero && !isAdmin) {
            showToast('El cajero no genera preventas. Use Completar venta o cargue un ticket PRV.', 'error');
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/preventas`, {
                method: 'POST',
                headers: { ...authHeader(), 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    items: cart,
                    total: calculateTotal(),
                    vendedor_id: vendedorToUse?.id || undefined
                })
            });

            if (res.status === 401) { logout(); return; }

            if (res.ok) {
                const data = await res.json();
                const itemsToPrint = cart;
                const totalToPrint = calculateTotal();

                setShowVendedorPreventaModal(false);
                setVendedorPreventaInput('');
                setVendedorPreventaResolved(null);
                vendedorScanBufferRef.current = '';
                setCart([]);
                beginPrintLock();

                printTicket({
                    id: data.preventaId,
                    vendedor: normalizeStaffCode(vendedorToUse?.codigo) || codigoPersonal(user),
                    items: itemsToPrint,
                    total: totalToPrint,
                    date: new Date()
                });

                showToast(`Preventa #${data.preventaId} generada. Imprimiendo ticket...`, 'success');
            } else {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.error || 'No se pudo crear la preventa');
            }
        } catch (err) {
            showToast(err.message || 'No se pudo crear la preventa. Por favor intente nuevamente.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const printTicket = (ticketData) => {
        beginPrintLock();
        const printWindow = window.open('', '_blank', 'width=350,height=700');
        if (!printWindow) {
            setIsPrinting(false);
            alert('Por favor habilite las ventanas emergentes para imprimir el ticket.');
            return;
        }

        // Generate barcode code: PRV + ID padded to 8 digits
        const barcodeValue = `PRV${String(ticketData.id).padStart(8, '0')}`;

        const html = `
            <!DOCTYPE html>
            <html>
                <head>
                    <title>Ticket Preventa #${ticketData.id}</title>
                    <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"></script>
                    <style>
                        @page { margin: 0; }
                        body { 
                            font-family: 'Arial Black', 'Arial', sans-serif; 
                            width: 76mm; 
                            margin: 3mm; 
                            font-size: 14px; 
                            line-height: 1.4;
                            font-weight: bold;
                        }
                        .header { text-align: center; margin-bottom: 12px; }
                        .store-name { font-size: 22px; font-weight: 900; letter-spacing: 1px; }
                        .store-info { font-size: 12px; font-weight: normal; }
                        .banner {
                            text-align: center;
                            background: #111;
                            color: #fff;
                            font-size: 12px;
                            font-weight: 900;
                            letter-spacing: 0.5px;
                            padding: 6px 4px;
                            margin: 8px 0 0;
                        }
                        .title { 
                            font-size: 22px; 
                            font-weight: 900; 
                            margin: 0 0 8px; 
                            padding: 8px 4px;
                            border: 3px dashed #000;
                            border-top: none;
                            text-align: center;
                            letter-spacing: 2px;
                        }
                        .info { font-size: 13px; margin-bottom: 3px; text-align: left; font-weight: 600; }
                        .table { width: 100%; margin-top: 12px; border-collapse: collapse; }
                        .table th { 
                            text-align: left; 
                            border-bottom: 2px solid #000; 
                            font-size: 13px; 
                            font-weight: 900;
                            padding: 4px 0;
                        }
                        .table td { 
                            text-align: left; 
                            padding: 4px 0; 
                            vertical-align: top; 
                            font-size: 13px;
                            font-weight: 600;
                        }
                        .qty { width: 12%; }
                        .desc { width: 55%; }
                        .price { width: 33%; text-align: right; }
                        .total-section { 
                            margin-top: 12px; 
                            border-top: 2px solid #000; 
                            padding-top: 8px; 
                            text-align: right; 
                        }
                        .total-line { 
                            font-weight: 900; 
                            font-size: 20px; 
                            letter-spacing: 1px;
                        }
                        .footer { 
                            margin-top: 15px; 
                            text-align: center; 
                            font-size: 14px; 
                            font-weight: 900;
                            padding: 8px;
                            background: #f0f0f0;
                            border-radius: 4px;
                        }
                        .barcode-section { 
                            margin-top: 15px; 
                            text-align: center; 
                            padding: 15px 5px;
                            border: 2px solid #000;
                            background: #fff;
                        }
                        .barcode-label {
                            font-size: 14px;
                            font-weight: 900;
                            color: #000;
                            margin-bottom: 8px;
                            text-transform: uppercase;
                            letter-spacing: 1px;
                        }
                        #barcode {
                            max-width: 100%;
                        }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <div class="store-name">ERP MINIMARKET</div>
                        <div class="store-info">Sucursal Central</div>
                    </div>
                    
                    <div class="info">Fecha: ${ticketData.date.toLocaleString('es-CL')}</div>
                    ${ticketData.vendedor ? `<div class="info">Atendido por: ${String(ticketData.vendedor).replace(/[<>&"]/g, '')}</div>` : ''}
                    
                    <div class="banner">NO ES BOLETA · PENDIENTE DE PAGO</div>
                    <div class="title">PREVENTA #${ticketData.id}</div>

                    <table class="table">
                        <thead>
                            <tr>
                                <th class="qty">Cant</th>
                                <th class="desc">Producto</th>
                                <th class="price">Subtotal</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${ticketData.items.map(item => `
                                <tr>
                                    <td class="qty">${item.quantity}</td>
                                    <td class="desc">${item.nombre.substring(0, 22)}</td>
                                    <td class="price">${formatCurrency(item.price * item.quantity)}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>

                    <div class="total-section">
                        <div class="total-line">TOTAL: ${formatCurrency(ticketData.total)}</div>
                    </div>

                    <div class="footer">
                        ★ PASE A CAJA CON ESTE TICKET ★
                    </div>

                    <div class="barcode-section">
                        <div class="barcode-label">PRESENTE EN CAJA</div>
                        <svg id="barcode"></svg>
                    </div>

                    <div style="text-align: center; margin-top: 10px; font-size: 11px; font-weight: normal;">
                        Código: ${barcodeValue}<br/>
                        Validez: 24 horas
                    </div>
                    
                    <script>
                        window.onload = function() {
                            // Generate barcode
                            JsBarcode("#barcode", "${barcodeValue}", {
                                format: "CODE128",
                                width: 2,
                                height: 50,
                                displayValue: false,
                                margin: 5
                            });
                            // Print after barcode renders
                            setTimeout(function() {
                                window.print();
                                setTimeout(function() { window.close(); }, 500);
                            }, 300);
                        }
                    </script>
                </body>
            </html>
        `;

        printWindow.document.write(html);
        printWindow.document.close();
    };

    /** Ticket físico orientativo para preventa/caja (sin registro en caja). */
    const printEnvasesTicket = (monto = ENVASE_TICKET_MONTO_CLP, titulo = 'ENVASE RETORNABLE') => {
        beginPrintLock();
        const printWindow = window.open('', '_blank', 'width=350,height=700');
        if (!printWindow) {
            setIsPrinting(false);
            alert('Por favor habilite las ventanas emergentes para imprimir.');
            return;
        }
        const noteHtml =
            'Documento orientativo. Para devolución con código ENV, el cliente presenta el ticket en caja.';

        const html = `<!DOCTYPE html><html><head><title>Ticket envases</title>
            <style>
            @page { margin: 0; }
            body { font-family: Arial Black, Arial, sans-serif; width: 76mm; margin: 3mm; font-size: 14px; line-height: 1.45; font-weight: bold; }
            .header { text-align: center; margin-bottom: 12px; }
            .store-name { font-size: 22px; font-weight: 900; letter-spacing: 1px; }
            .title { font-size: 18px; font-weight: 900; margin: 12px 0; padding: 10px 0; border-top: 3px solid #000; border-bottom: 3px solid #000; text-align: center; }
            .info { font-size: 13px; font-weight: 600; margin: 8px 0; }
            .amount { font-size: 28px; text-align: center; margin: 16px 0; font-weight: 900; }
            .note { font-size: 11px; font-weight: 600; margin-top: 14px; border-top: 1px dashed #000; padding-top: 10px; }
            .footer { margin-top: 16px; text-align: center; font-size: 12px; font-weight: 600; }
            </style></head><body>
            <div class="header"><div class="store-name">ERP MINIMARKET</div></div>
            <div class="title">${String(titulo).replace(/</g, '')}</div>
            <div class="info">Fecha: ${new Date().toLocaleString('es-CL')}</div>
            ${codigoPersonal(user) ? `<div class="info">Atendido por: ${codigoPersonal(user)}</div>` : ''}
            <p class="amount">${formatCurrency(monto)}</p>
            <div class="note">${noteHtml}</div>
            <div class="footer">Documento orientativo — conserve si procede</div>
            <script>window.onload=function(){window.print();setTimeout(function(){window.close();},500);}</script>
            </body></html>`;
        printWindow.document.write(html);
        printWindow.document.close();
    };

    // Print Sale Receipt
    const printSaleReceipt = (saleData) => {
        beginPrintLock();
        const printWindow = window.open('', '_blank', 'width=350,height=700');
        if (!printWindow) {
            setIsPrinting(false);
            alert('Por favor habilite las ventanas emergentes para imprimir.');
            return;
        }

        const preventaInfo = saleData.preventaId ? '<div class="info">Preventa: #' + saleData.preventaId + '</div>' : '';
        const staffCodes = [saleData.cajeroCodigo, saleData.vendedorCodigo]
            .map((code) => String(code || '').replace(/[<>&"]/g, ''))
            .filter((code, index, all) => code && all.indexOf(code) === index);
        const staffInfo = staffCodes.map((code) => {
            const label = code.startsWith('CAJ') ? 'Cajero' : 'Vendedor';
            return '<div class="info">' + label + ': ' + code + '</div>';
        }).join('');
        
        // Build payment methods HTML
        let paymentMethodsHtml = '';
        if (saleData.payments && saleData.payments.length > 0) {
            paymentMethodsHtml = saleData.payments.map(p => 
                '<div><strong>' + p.name + ':</strong> ' + formatCurrency(p.amount) + '</div>'
            ).join('');
            const vueltoEfec = saleData.vuelto_efectivo || 0;
            if (saleData.change > 0 && !vueltoEfec) {
                paymentMethodsHtml += '<div style="margin-top:5px;border-top:1px dashed #ccc;padding-top:5px;"><strong>Vuelto:</strong> ' + formatCurrency(saleData.change) + '</div>';
            }
            if (vueltoEfec > 0) {
                paymentMethodsHtml += '<div style="margin-top:5px;border-top:1px dashed #ccc;padding-top:5px;"><strong>Vuelto en efectivo (desde caja):</strong> ' + formatCurrency(vueltoEfec) + '</div>';
            }
        }
        
        const itemsHtml = saleData.items
            .map((item) => {
                const line = item.venta_por_peso ? item.price || 0 : item.price * item.quantity;
                return (
                    '<tr><td class="qty">' +
                    item.quantity +
                    '</td><td class="desc">' +
                    item.nombre.substring(0, 22) +
                    '</td><td class="price">' +
                    formatCurrency(line) +
                    '</td></tr>'
                );
            })
            .join('');

        let envaseRowsHtml = '';
        if (saleData.boletaCargoEnvaseMonto > 0) {
            envaseRowsHtml +=
                '<tr><td class="qty">1</td><td class="desc">' +
                (saleData.boletaCargoEnvaseMonto === ENVASE_BIDON_MONTO_CLP
                    ? 'Cargo envase bidón 20L'
                    : 'Cargo envase retornable') +
                '</td><td class="price">' +
                formatCurrency(saleData.boletaCargoEnvaseMonto) +
                '</td></tr>';
        }
        if (saleData.boletaDescuentoDevolucionEnvase) {
            envaseRowsHtml +=
                '<tr><td class="qty">1</td><td class="desc">Devolución envase</td><td class="price">' +
                formatCurrency(-ENVASE_TICKET_MONTO_CLP) +
                '</td></tr>';
        }

        const html = '<!DOCTYPE html><html><head><title>Boleta Venta #' + saleData.id + '</title>' +
            '<style>' +
            '@page { margin: 0; }' +
            'body { font-family: Arial Black, Arial, sans-serif; width: 76mm; margin: 3mm; font-size: 14px; line-height: 1.4; font-weight: bold; }' +
            '.header { text-align: center; margin-bottom: 12px; }' +
            '.store-name { font-size: 22px; font-weight: 900; letter-spacing: 1px; }' +
            '.store-info { font-size: 12px; font-weight: normal; }' +
            '.banner { text-align: center; border: 3px solid #000; font-weight: 900; letter-spacing: 2px; padding: 6px 4px; margin: 8px 0 0; font-size: 13px; }' +
            '.title { font-size: 20px; font-weight: 900; margin: 0 0 8px; padding: 8px 4px; background: #000; color: #fff; text-align: center; letter-spacing: 2px; }' +
            '.info { font-size: 13px; margin-bottom: 3px; text-align: left; font-weight: 600; }' +
            '.table { width: 100%; margin-top: 12px; border-collapse: collapse; }' +
            '.table th { text-align: left; border-bottom: 2px solid #000; font-size: 13px; font-weight: 900; padding: 4px 0; }' +
            '.table td { text-align: left; padding: 4px 0; vertical-align: top; font-size: 13px; font-weight: 600; }' +
            '.qty { width: 12%; } .desc { width: 55%; } .price { width: 33%; text-align: right; }' +
            '.total-section { margin-top: 12px; border-top: 2px solid #000; padding-top: 8px; }' +
            '.total-line { font-weight: 900; font-size: 22px; text-align: right; }' +
            '.payment-info { margin-top: 10px; padding: 8px; background: #f5f5f5; border-radius: 4px; font-size: 13px; }' +
            '.footer { margin-top: 20px; text-align: center; font-size: 13px; font-weight: 600; }' +
            '</style></head><body>' +
            '<div class="header"><div class="store-name">ERP MINIMARKET</div><div class="store-info">Sucursal Central</div></div>' +
            '<div class="banner">COMPROBANTE DE PAGO</div>' +
            '<div class="title">BOLETA DE VENTA</div>' +
            '<div class="info">Venta #' + saleData.id + '</div>' +
            '<div class="info">Fecha: ' + saleData.date.toLocaleString('es-CL') + '</div>' +
            staffInfo +
            preventaInfo +
            '<table class="table"><thead><tr><th class="qty">Cant</th><th class="desc">Producto</th><th class="price">Subtotal</th></tr></thead><tbody>' +
            itemsHtml +
            envaseRowsHtml +
            '</tbody></table>' +
            '<div class="total-section"><div class="total-line">TOTAL: ' + formatCurrency(saleData.total) + '</div></div>' +
            '<div class="payment-info"><strong>Forma de pago:</strong><br/>' + paymentMethodsHtml + '</div>' +
            '<div class="footer">¡Gracias por su compra!<br/><span style="font-size: 11px; font-weight: normal;">PAGADO. Conserve este comprobante. No se cobra en caja.</span></div>' +
            '<script>window.onload = function() { window.print(); setTimeout(function() { window.close(); }, 500); }</script>' +
            '</body></html>';

        printWindow.document.write(html);
        printWindow.document.close();
    };

    const handleCompleteSale = () => {
        if (cart.length === 0) return;
        if (saleRequiresVnd && !selectedVendedor) {
            showToast('Debe escanear el código de vendedor (VND) antes de completar la venta.', 'error');
            return;
        }
        if (requiereCajaOperativa && !puedeEjecutarCobro) {
            showToast('Abra la caja antes de cobrar.', 'error');
            return;
        }
        if (isVendedorRole && !activePreventaId) {
            showToast('Cargue una preventa antes de cobrar.', 'error');
            return;
        }
        scanFocusPausedRef.current = true;
        setPaymentData({ efectivo: '', tarjeta_debito: '', tarjeta_credito: '' });
        setVueltoEfectivo('');
        if (requiereCajaOperativa && cajaSession?.isOpen) {
            refreshEfectivoCaja();
        }
        setShowPaymentModal(true);
    };

    /**
     * Enter on an empty scan field opens payment.
     * A scanner's trailing Enter still has the code in the field, so it does not charge.
     */
    const handleScanFieldKeyDown = (e) => {
        if (isPrinting) {
            e.preventDefault();
            return;
        }
        if (e.key !== 'Enter') return;
        e.preventDefault();
        if (scanFocusPausedRef.current || showPaymentModal) return;
        const pending = (e.currentTarget.value || '').trim();
        if (pending) return;
        if (!canCompleteSaleWithLoadedPreventa || loading || cart.length === 0) return;
        handleCompleteSale();
    };

    const restoreScanFocus = () => {
        if (scanFocusPausedRef.current || isPrinting) return;
        const input = searchInputRef.current;
        if (!input || input.disabled) return;
        const active = document.activeElement;
        if (active === input) return;
        const tag = active?.tagName;
        if (
            active &&
            active !== document.body &&
            (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || active.isContentEditable)
        ) {
            return;
        }
        input.focus({ preventScroll: true });
    };

    /** Keep only digits so a typed "2.000" or "200,0" is stored as 2000. */
    const soloDigitosMonto = (raw) => String(raw ?? '').replace(/\D/g, '');

    const sumarBilleteRecibido = (amount) => {
        const current = Math.round(parseMontoClp(paymentData.efectivo) || 0);
        setPaymentData({ ...paymentData, efectivo: String(current + amount) });
    };

    /** Numeric payment amounts (CLP integers) from modal text fields. */
    const getPaymentAmounts = () => ({
        efectivo: Math.round(parseMontoClp(paymentData.efectivo) || 0),
        tarjeta_debito: Math.round(parseMontoClp(paymentData.tarjeta_debito) || 0),
        tarjeta_credito: Math.round(parseMontoClp(paymentData.tarjeta_credito) || 0),
    });

    const getGiroIngresado = () => {
        if (!cajeroPuedeEjecutarCobro) return 0;
        const n = Math.max(0, Math.round(parseMontoClp(vueltoEfectivo) || 0));
        return GIRO_TARJETA_QUICK_AMOUNTS.includes(n) ? n : 0;
    };

    /**
     * Debit/credit may be the boleta or the exact vale.
     * If the cashier typed only the boleta and picked a giro button, add giro onto the card.
     * If they already typed boleta+giro (the vale), do not add twice.
     */
    const applyGiroToCardPayments = (payments, saleTotal, giroRaw) => {
        const giro = Math.max(0, Math.round(Number(giroRaw) || 0));
        const next = {
            efectivo: Math.round(Number(payments?.efectivo) || 0),
            tarjeta_debito: Math.round(Number(payments?.tarjeta_debito) || 0),
            tarjeta_credito: Math.round(Number(payments?.tarjeta_credito) || 0),
        };
        const cardBefore = next.tarjeta_debito + next.tarjeta_credito;
        if (giro <= 0 || cardBefore <= 0) {
            return { payments: next, giro, giroAdded: 0 };
        }
        const paidBefore = next.efectivo + cardBefore;
        const alreadyIncluded = paidBefore >= saleTotal + giro - 1;
        if (alreadyIncluded) {
            return { payments: next, giro, giroAdded: 0 };
        }
        const addTo = next.tarjeta_debito > 0 || next.tarjeta_credito <= 0
            ? 'tarjeta_debito'
            : 'tarjeta_credito';
        next[addTo] += giro;
        return { payments: next, giro, giroAdded: giro };
    };

    const getPaymentsForCharge = (saleTotal = calculateTotal()) => {
        const typed = getPaymentAmounts();
        const giro = getGiroIngresado();
        const card = typed.tarjeta_debito + typed.tarjeta_credito;
        const cashCoversBoleta = typed.efectivo > 0 && typed.efectivo + card + 1 >= saleTotal;
        // Cash sale: the yellow amount is change from the drawer, not a card cash advance.
        if (giro > 0 && card <= 0 && cashCoversBoleta) {
            const efectivo = Math.max(typed.efectivo, saleTotal + giro);
            return {
                payments: { efectivo, tarjeta_debito: 0, tarjeta_credito: 0 },
                giro,
                giroAdded: 0,
                cashChange: efectivo - saleTotal,
            };
        }
        return { ...applyGiroToCardPayments(typed, saleTotal, giro), cashChange: 0 };
    };

    /**
     * Typed card amount cannot exceed remaining ticket + selected giro.
     * Cash may exceed the ticket (normal change).
     */
    const getCardValeLimits = (saleTotal = calculateTotal()) => {
        const p = getPaymentAmounts();
        const giro = getGiroIngresado();
        const efectivoAplicado = Math.min(p.efectivo, saleTotal);
        const remainingBoleta = Math.max(0, saleTotal - efectivoAplicado);
        const maxCard = remainingBoleta + giro;
        const cardTotal = p.tarjeta_debito + p.tarjeta_credito;
        return {
            giro,
            remainingBoleta,
            maxCard,
            cardTotal,
            expectedVale: maxCard,
            cardExceeds: cardTotal > maxCard,
        };
    };

    const expectedBoletaForCardField = (field, saleTotal = calculateTotal()) => {
        const p = getPaymentAmounts();
        const efectivoAplicado = Math.min(p.efectivo, saleTotal);
        const otherCard = field === 'tarjeta_debito' ? p.tarjeta_credito : p.tarjeta_debito;
        return Math.max(0, Math.round(saleTotal - efectivoAplicado - otherCard));
    };

    /** Cash "Boleta" fills the ticket remainder after debit and credit already typed. */
    const expectedBoletaForEfectivo = (saleTotal = calculateTotal()) => {
        const p = getPaymentAmounts();
        return Math.max(0, Math.round(saleTotal - p.tarjeta_debito - p.tarjeta_credito));
    };

    const getTotalPaid = () => {
        const { payments: p } = getPaymentsForCharge();
        return p.efectivo + p.tarjeta_debito + p.tarjeta_credito;
    };

    const getTotalPaidTyped = () => {
        const p = getPaymentAmounts();
        return p.efectivo + p.tarjeta_debito + p.tarjeta_credito;
    };

    /** Same vuelto split as backend saleController — all change is physical cash out. */
    const computeVueltoDrawerImpact = (saleTotal, payments, vueltoManualRaw) => {
        const efectivoPagado = Math.round(Number(payments?.efectivo) || 0);
        const totalPaid =
            efectivoPagado +
            Math.round(Number(payments?.tarjeta_debito) || 0) +
            Math.round(Number(payments?.tarjeta_credito) || 0);
        const vueltoTotal = Math.max(0, Math.round(totalPaid - saleTotal));
        const efectivoExcedente = Math.max(0, efectivoPagado - saleTotal);
        let vueltoEfectivo = Math.min(vueltoTotal, efectivoExcedente);
        const vueltoManual = Math.max(0, Math.round(Number(vueltoManualRaw) || 0));
        if (vueltoManual > 0 && vueltoManual <= vueltoTotal + 1) {
            vueltoEfectivo = Math.min(vueltoManual, efectivoExcedente, vueltoTotal);
        }
        return { totalPaid, vueltoTotal, vueltoEfectivo, efectivoPagado };
    };

    /** Cash shortfall to deliver change: disponible + efectivo recibido en venta >= vuelto. */
    const computeEfectivoFaltanteParaVuelto = (efectivoDisponible, saleTotal, payments, vueltoManualRaw) => {
        if (efectivoDisponible == null || !Number.isFinite(Number(efectivoDisponible))) return null;
        const { vueltoTotal, efectivoPagado } = computeVueltoDrawerImpact(
            saleTotal,
            payments,
            vueltoManualRaw
        );
        if (vueltoTotal <= 0) {
            return { falta: 0, vueltoTotal: 0, efectivoDisponible: Number(efectivoDisponible), efectivoPagado };
        }
        const falta = Math.max(0, vueltoTotal - Number(efectivoDisponible) - efectivoPagado);
        return {
            falta,
            vueltoTotal,
            efectivoDisponible: Number(efectivoDisponible),
            efectivoPagado,
        };
    };

    const refreshEfectivoCaja = async () => {
        try {
            const res = await fetch(`${API_URL}/api/cajas/status`, { headers: authHeader() });
            if (res.status === 401) {
                logout();
                return;
            }
            if (res.ok) {
                const data = await res.json();
                setCajaSession((prev) => (prev?.isOpen ? { ...prev, ...data } : data));
            }
        } catch (err) {
            console.error(err);
        }
    };

    const fetchMovimientosEfectivo = async () => {
        setMovimientosEfectivoLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/cajas/movimientos-efectivo`, { headers: authHeader() });
            if (res.status === 401) {
                logout();
                return;
            }
            const data = await res.json().catch(() => ({}));
            setMovimientosEfectivo(Array.isArray(data.movimientos) ? data.movimientos : []);
        } catch (err) {
            console.error(err);
            setMovimientosEfectivo([]);
        } finally {
            setMovimientosEfectivoLoading(false);
        }
    };

    const openMovimientosListModal = () => {
        setShowMovimientosListModal(true);
        fetchMovimientosEfectivo();
    };

    const abrirInyeccionDesdePago = (montoSugerido) => {
        setMovimientoEfectivoTipo('inyeccion_efectivo');
        setMovimientoEfectivoDesglose(createEmptyDesglose());
        const sugerido = montoSugerido > 0 ? Math.round(montoSugerido) : null;
        setMovimientoEfectivoSugerido(sugerido);
        setMovimientoEfectivoMonto(sugerido != null ? String(sugerido) : '');
        setShowMovimientoEfectivoModal(true);
    };

    // Get payment methods summary for receipt
    const getPaymentMethodsSummary = () => {
        const p = getPaymentsForCharge().payments;
        const methods = [];
        if (p.efectivo > 0) methods.push({ name: 'Efectivo', amount: p.efectivo });
        if (p.tarjeta_debito > 0) methods.push({ name: 'Tarjeta Débito', amount: p.tarjeta_debito });
        if (p.tarjeta_credito > 0) methods.push({ name: 'Tarjeta Crédito', amount: p.tarjeta_credito });
        return methods;
    };

    const confirmSale = async () => {
        setLoading(true);
        if (requiereCajaOperativa && !puedeEjecutarCobro) {
            showToast(
                cajaSession?.sales_blocked_reason ||
                    'No puede cobrar: active la sucursal y marque la caja como operativa en Gestión de sucursales o en Cajas.',
                'error'
            );
            setLoading(false);
            return;
        }
        const saleTotal = calculateTotal();
        const giroIngresado = getGiroIngresado();
        if (giroIngresado > GIRO_TARJETA_MAX_CLP) {
            alert(`El vuelto sugerido no puede ser mayor a ${formatCurrency(GIRO_TARJETA_MAX_CLP)}.`);
            setLoading(false);
            return;
        }
        const valeLimits = getCardValeLimits(saleTotal);
        if (valeLimits.cardExceeds) {
            alert(
                `El monto del vale no puede ser mayor a ${formatCurrency(valeLimits.maxCard)} ` +
                `(boleta pendiente ${formatCurrency(valeLimits.remainingBoleta)}` +
                (valeLimits.giro > 0 ? ` + vuelto ${formatCurrency(valeLimits.giro)}` : '') +
                ').'
            );
            setLoading(false);
            return;
        }
        const { payments: paymentsNumeric, cashChange } = getPaymentsForCharge(saleTotal);
        const totalPaid = paymentsNumeric.efectivo + paymentsNumeric.tarjeta_debito + paymentsNumeric.tarjeta_credito;
        const vueltoRegistrado = cashChange > 0 ? cashChange : giroIngresado;
        const cardPaid = paymentsNumeric.tarjeta_debito + paymentsNumeric.tarjeta_credito;
        const maxVueltoPermitido = Math.max(0, Math.round(totalPaid - saleTotal));
        if (vueltoRegistrado > 0 && cardPaid <= 0 && cashChange <= 0) {
            alert('Para entregar giro en efectivo, ingrese el monto de la boleta en tarjeta débito o crédito.');
            setLoading(false);
            return;
        }
        if (vueltoRegistrado > maxVueltoPermitido + 1) {
            alert('El vuelto en efectivo no puede superar lo cobrado menos el total de la venta.');
            setLoading(false);
            return;
        }
        if (cashChange <= 0 && vueltoRegistrado > 0 && saleTotal < 1000) {
            alert('La venta debe ser de al menos $1.000 para registrar vuelto en efectivo desde caja.');
            setLoading(false);
            return;
        }

        const vueltoManualParaChequeo = cajeroPuedeEjecutarCobro ? vueltoRegistrado : 0;
        const chequeoEfectivo = computeEfectivoFaltanteParaVuelto(
            cajaSession?.efectivo_disponible_en_caja,
            saleTotal,
            paymentsNumeric,
            vueltoManualParaChequeo
        );
        if (chequeoEfectivo && chequeoEfectivo.falta > 0) {
            alert(
                ocultarEfectivoSistema
                    ? `No hay suficiente efectivo en caja para entregar el vuelto.\n\n` +
                          `Vuelto a entregar: ${formatCurrency(chequeoEfectivo.vueltoTotal)}\n` +
                          `Faltan: ${formatCurrency(chequeoEfectivo.falta)}\n\n` +
                          `Use «Inyección de efectivo» (↓) antes de confirmar el pago.`
                    : `No hay suficiente efectivo en caja para entregar el vuelto.\n\n` +
                          `Disponible en caja: ${formatCurrency(chequeoEfectivo.efectivoDisponible)}\n` +
                          `Efectivo recibido (esta venta): ${formatCurrency(chequeoEfectivo.efectivoPagado)}\n` +
                          `Vuelto a entregar: ${formatCurrency(chequeoEfectivo.vueltoTotal)}\n` +
                          `Faltan: ${formatCurrency(chequeoEfectivo.falta)}\n\n` +
                          `Use «Inyección de efectivo» (↓) antes de confirmar el pago.`
            );
            setLoading(false);
            return;
        }

        const change = vueltoRegistrado > 0 ? 0 : Math.max(0, totalPaid - saleTotal);
        const cargoMonto =
            opcionesSesionCajaVentasActivas && boletaCargoEnvaseMonto > 0 ? boletaCargoEnvaseMonto : 0;
        const descBoleta = opcionesSesionCajaVentasActivas && boletaDescuentoDevolucionEnvase;
        let res;
        try {
            res = await fetch(`${API_URL}/api/sales`, {
                method: 'POST',
                headers: { ...authHeader(), 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    items: cart,
                    total: saleTotal,
                    cajaId: cajaSession?.session?.caja_id || null,
                    preventa_id: activePreventaId,
                    vendedor_id: selectedVendedor?.id ?? null,
                    payments: paymentsNumeric,
                    totalPaid: totalPaid,
                    vuelto_efectivo: vueltoRegistrado,
                    envase: cargoMonto > 0
                        ? { tipo: 'cargo', monto: cargoMonto }
                        : descBoleta
                            ? { tipo: 'devolucion', monto: ENVASE_TICKET_MONTO_CLP }
                            : null,
                })
            });

            if (res.status === 401) { logout(); return; }

            if (res.ok) {
                const saleResult = await res.json();
                
                // Print sale receipt with multiple payment methods
                printSaleReceipt({
                    id: saleResult.ventaId || saleResult.id || Date.now(),
                    date: new Date(),
                    cajeroCodigo: codigoPersonal(user),
                    vendedorCodigo: normalizeStaffCode(selectedVendedor?.codigo),
                    preventaId: activePreventaId,
                    items: cart,
                    total: saleTotal,
                    payments: getPaymentMethodsSummary(),
                    totalPaid: totalPaid,
                    change: change,
                    vuelto_efectivo: vueltoRegistrado,
                    boletaCargoEnvaseMonto: cargoMonto,
                    boletaDescuentoDevolucionEnvase: descBoleta,
                });

                const ventaId = saleResult.ventaId || saleResult.id;
                setUltimaVentaAnulable(ventaId ? { id: ventaId, total: saleTotal } : null);
                alert('Venta Completada Exitosamente!');
                setCart([]);
                setActivePreventaId(null);
                setSelectedVendedor(null);
                setBoletaCargoEnvaseMonto(0);
                setBoletaDescuentoDevolucionEnvase(false);
                setVueltoEfectivo('');
                setShowPaymentModal(false);
                if (requiereCajaOperativa) {
                    refreshEfectivoCaja();
                }
            } else {
                throw new Error('Failed');
            }
        } catch (err) {
            let errMsg = 'Error al procesar la venta';
            try {
                if (res && !res.ok) {
                    const data = await res.json().catch(() => ({}));
                    errMsg = data.error || errMsg;
                    if (data.detalle?.falta_efectivo > 0) {
                        errMsg += ocultarEfectivoSistema
                            ? `\n\nVuelto: ${formatCurrency(data.detalle.vuelto_requerido)}\n` +
                              `Faltan: ${formatCurrency(data.detalle.falta_efectivo)}`
                            : `\n\nDisponible: ${formatCurrency(data.detalle.efectivo_disponible)}\n` +
                              `Vuelto: ${formatCurrency(data.detalle.vuelto_requerido)}\n` +
                              `Faltan: ${formatCurrency(data.detalle.falta_efectivo)}`;
                    }
                } else if (err?.message) {
                    errMsg = err.message;
                }
            } catch (_) {}
            alert(errMsg);
        } finally {
            setLoading(false);
        }
    };

    const loadPreventa = async (id) => {
        if (isPreventaPrevendedor) {
            showToast('Su rol solo genera preventas. Para cobrar una preventa use personal de caja.', 'error');
            return;
        }
        try {
            const res = await fetch(`${API_URL}/api/preventas/${id}`, {
                headers: authHeader()
            });
            if (res.status === 401) { logout(); return; }
            if (!res.ok) {
                showToast('Preventa no encontrada o no disponible', 'error');
                return;
            }

            const data = await res.json();

            if (data.estado === false) {
                showToast('Esta preventa ya fue cobrada y no puede cargarse.', 'error');
                return;
            }

            // Transform items to match cart structure (products, promos, weight sales)
            const cartItems = data.items.map(i => {
                if (i.promocion_id) {
                    const promoCartId = i.producto_id
                        ? `promo-${i.promocion_id}-${i.producto_id}`
                        : `promo-${i.promocion_id}`;
                    return {
                        id: promoCartId,
                        isPromo: true,
                        promocion_id: i.promocion_id,
                        nombre: i.promocion_nombre
                            ? `Promo: ${i.promocion_nombre}`
                            : (i.product_name || 'Promoción'),
                        price: parseFloat(i.precio_unitario),
                        quantity: i.cantidad,
                        valor_producto: i.precio_unitario,
                    };
                }
                if (i.gramos != null && Number(i.gramos) > 0) {
                    return {
                        id: i.producto_id,
                        nombre: i.product_name,
                        price: parseFloat(i.precio_total),
                        quantity: 1,
                        venta_por_peso: true,
                        grams: Number(i.gramos),
                        valor_producto: i.precio_unitario,
                    };
                }
                return {
                    id: i.producto_id,
                    nombre: i.product_name,
                    price: parseFloat(i.precio_unitario),
                    quantity: i.cantidad,
                    valor_producto: i.precio_unitario,
                };
            });

            setCart(applyAutoPacks(cartItems, promotions).cart);
            setActivePreventaId(data.id);
            if (data.usuario_id && (data.vendedor_codigo || data.vendedor_name)) {
                setSelectedVendedor({
                    id: data.usuario_id,
                    name: data.vendedor_name,
                    codigo: data.vendedor_codigo || '',
                });
            }
            setBoletaCargoEnvaseMonto(0);
            setBoletaDescuentoDevolucionEnvase(false);
            setShowPreventaModal(false);
            showToast(`Preventa #${data.id} cargada correctamente`, 'success');
            setSearchTerm(''); // Clear search after loading preventa
        } catch (err) {
            console.error(err);
            showToast('Error al cargar la preventa', 'error');
        }
    };

    // Helpers
    const authHeader = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token')}` });

    const anularVenta = async (ventaId) => {
        if (!ventaId) return;
        const ok = window.confirm(
            `¿Anular la venta #${ventaId}?\n\nÚselo si imprimió el ticket y el cliente no pudo pagar. El stock vuelve y, si venía de una preventa, queda pendiente para escanearla de nuevo.`
        );
        if (!ok) return;
        try {
            setLoading(true);
            const res = await fetch(`${API_URL}/api/sales/${ventaId}/anular`, {
                method: 'POST',
                headers: { ...authHeader(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ motivo: 'Cliente no pudo pagar' }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || 'No se pudo anular la venta');
            setUltimaVentaAnulable(null);
            if (requiereCajaOperativa) refreshEfectivoCaja();
            showToast(
                data.preventaReabierta
                    ? `Venta #${ventaId} anulada. Preventa #${data.preventaReabierta} quedó pendiente: escanéela de nuevo.`
                    : `Venta #${ventaId} anulada. El stock volvió.`,
                'success'
            );
        } catch (err) {
            showToast(err.message || 'No se pudo anular la venta', 'error');
        } finally {
            setLoading(false);
        }
    };
    const calculateCartSubtotal = () =>
        cart.reduce((sum, item) => {
            if (item.venta_por_peso) return sum + (item.price || 0);
            return sum + item.price * item.quantity;
        }, 0);

    /** Total impreso y cobrado redondeado a múltiplos de $10 (ej. 2814→2810, 546→550). */
    const roundBoletaClp = (n) => {
        const x = Math.round(Number(n) || 0);
        return Math.max(0, Math.round(x / 10) * 10);
    };

    const calculateTotal = () => {
        let t = calculateCartSubtotal();
        if (opcionesSesionCajaVentasActivas) {
            if (boletaCargoEnvaseMonto > 0) t += boletaCargoEnvaseMonto;
            if (boletaDescuentoDevolucionEnvase) t -= ENVASE_TICKET_MONTO_CLP;
        }
        return roundBoletaClp(Math.max(0, t));
    };
    const formatCurrency = (amount) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(amount);

    const commitCart = (nextCart) => {
        const result = applyAutoPacks(nextCart, promotions);
        const packed = result.cart;
        const applied = result.newlyApplied || result.newlyApplied || [];
        applied.forEach((a) => {
            const label = String(a.nombre || '').replace(/^Promo:\s*/i, '');
            showToast(`Promo aplicada: ${label} — ${a.packQty} un. por ${formatCurrency(a.monto)}`, 'success');
        });
        return packed;
    };

    const promoHasStock = (row) => {
        const stock = parseInt(row?.stock_actual, 10) || 0;
        const need = parseInt(row?.cantidad, 10) || 1;
        return stock <= 0 || stock >= need;
    };

    const addPromoToCart = async (promo, selectedItem = null, addFullPack = false) => {
        try {
            const sucursalId = cajaSession?.session?.sucursal_id || availableCajas[0]?.sucursal_id || 1;
            const res = await fetch(`${API_URL}/api/promotions/${promo.id}?sucursal_id=${sucursalId}`, { headers: authHeader() });
            if (res.status === 401) { logout(); return; }
            if (!res.ok) {
                showToast('No se pudo cargar la oferta', 'error');
                return;
            }
            const promoDetail = await res.json();
            const packItems = (promoDetail.items || []).filter((item) => !item.extra);
            const options = packItems.length ? packItems : (promoDetail.items || []);

            if (options.length > 1 && !selectedItem && !addFullPack) {
                const withStock = options.filter(promoHasStock);
                if (!withStock.length) {
                    showToast('No hay stock suficiente para esta oferta', 'error');
                    return;
                }
                setPromoSelectionModal({
                    promo,
                    detail: { ...promoDetail, items: options },
                    allHaveStock: options.every(promoHasStock),
                });
                return;
            }

            let chosen = null;
            if (addFullPack) {
                if (!options.every(promoHasStock)) {
                    showToast('No hay stock suficiente para el pack completo', 'error');
                    return;
                }
            } else {
                chosen = selectedItem
                    ? options.find((item) => Number(item.producto_id) === Number(selectedItem.producto_id))
                    : options[0];
                if (chosen && !promoHasStock(chosen)) {
                    showToast('No hay stock suficiente para esta oferta', 'error');
                    return;
                }
            }

            const lineItem = selectedItem
                || (!addFullPack && chosen
                    ? { producto_id: chosen.producto_id, cantidad: parseInt(chosen.cantidad, 10) || 1 }
                    : null);
            const promoCartId = lineItem
                ? `promo-${promo.id}-${lineItem.producto_id}`
                : `promo-${promo.id}`;
            const displayName = lineItem && chosen?.nombre && options.length > 1
                ? `Promo: ${promo.nombre} (${chosen.nombre})`
                : `Promo: ${promo.nombre}`;

            setCart((prev) => {
                const existing = prev.find((item) => item.id === promoCartId);
                if (existing) {
                    return prev.map((item) => (
                        item.id === promoCartId ? { ...item, quantity: item.quantity + 1 } : item
                    ));
                }
                return [...prev, {
                    id: promoCartId,
                    isPromo: true,
                    promocion_id: promo.id,
                    nombre: displayName,
                    price: parseFloat(promo.monto),
                    quantity: 1,
                    valor_producto: promo.monto,
                    ...(lineItem ? { promo_selected_item: lineItem } : {}),
                }];
            });
            setPromoSelectionModal(null);
            setSearchTerm('');
            setProducts([]);
            showToast(`Oferta agregada: ${promo.nombre}`, 'success');
            setTimeout(() => searchInputRef.current?.focus(), 50);
        } catch (err) {
            console.error(err);
            showToast('No se pudo agregar la oferta', 'error');
        }
    };

    /** Thermal-style “corte de turno” after closing register (76mm-oriented HTML print). */
    const printCorteTurno = (corte) => {
        if (!corte?.resumen_cierre) return;
        const printWindow = window.open('', '_blank', 'width=360,height=900');
        if (!printWindow) {
            showToast('Habilite ventanas emergentes para imprimir el corte de turno.', 'error');
            return;
        }
        const esc = (s) =>
            String(s ?? '')
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;');
        const fmtDt = (v) => {
            if (v == null || v === '') return '—';
            const d = v instanceof Date ? v : new Date(v);
            return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'medium' });
        };
        const r = corte.resumen_cierre;
        const egresosVueltoEfectivo = r.egresos_vuelto_efectivo || 0;
        const egresosVueltoTarjeta = r.egresos_vuelto_tarjeta || 0;
        // Giro deducted once in total ventas reales (single line on ticket).
        const totalVentasReales = resolveTotalVentasReales(corte);
        const cuadre = corte.cuadre_ventas_reales || {};
        const redondeoBoleta = Number(cuadre.redondeo_boleta) || 0;
        const envaseNetoBoleta = Number(cuadre.envase_neto_boleta) || 0;

        let extraVueltosRows = '';
        if (egresosVueltoEfectivo > 0) {
            extraVueltosRows += `<tr><td class="col-l" style="padding-left:8px; font-style:italic;">(-) Vuelto en efectivo</td><td class="col-r">-${formatCurrency(egresosVueltoEfectivo)}</td></tr>`;
        }
        if (egresosVueltoTarjeta > 0) {
            extraVueltosRows += `<tr><td class="col-l" style="padding-left:8px; font-style:italic;">(-) Giro tarjeta (efectivo)</td><td class="col-r">-${formatCurrency(egresosVueltoTarjeta)}</td></tr>`;
        }

        const formaRows = (corte.ventas_por_forma_pago || [])
            .map(
                (row) =>
                    `<tr><td class="col-l">${esc(row.forma)}</td><td class="col-r">${formatCurrency(row.monto)}</td></tr>`
            )
            .join('');
        const catRows = (corte.ventas_por_categoria || [])
            .map(
                (row) =>
                    `<tr><td class="col-l">${esc(row.categoria)}</td><td class="col-r">${formatCurrency(row.monto)}</td></tr>`
            )
            .join('');
        const contadoLine =
            r.efectivo_contado != null && Number.isFinite(Number(r.efectivo_contado))
                ? `<div class="row"><span class="col-l">Efectivo contado</span><span class="col-r">${formatCurrency(r.efectivo_contado)}</span></div>`
                : '';
        const forzadoBlock =
            r.cierre_forzado && r.diferencia_efectivo != null
                ? `<div class="meta" style="font-weight:800;text-align:center;margin:8px 0">*** CIERRE CON DESCUADRE ***</div>` +
                  `<div class="row"><span class="col-l">Efectivo esperado</span><span class="col-r">${formatCurrency(r.efectivo_esperado_en_caja ?? r.total_caja_mas_base)}</span></div>` +
                  `<div class="row bold"><span class="col-l">Diferencia</span><span class="col-r">${formatCurrency(r.diferencia_efectivo)}</span></div>`
                : '';
        const html =
            '<!DOCTYPE html><html><head><meta charset="utf-8"><title>Corte de turno</title>' +
            '<style>' +
            '@page { margin: 4mm; }' +
            'body { font-family: ui-monospace, "Cascadia Code", Consolas, monospace; width: 72mm; margin: 0 auto; padding: 2mm; font-size: 11px; line-height: 1.35; color: #000; }' +
            'h1 { text-align: center; font-size: 13px; margin: 0 0 6px; letter-spacing: 0.5px; }' +
            '.meta { margin: 2px 0; font-size: 10px; }' +
            '.sep { border: none; border-top: 1px dashed #000; margin: 8px 0; }' +
            '.sec { font-weight: 700; text-align: center; margin: 10px 0 6px; font-size: 11px; }' +
            'table { width: 100%; border-collapse: collapse; }' +
            'td { padding: 2px 0; vertical-align: top; }' +
            '.col-l { text-align: left; padding-right: 4px; }' +
            '.col-r { text-align: right; white-space: nowrap; font-weight: 600; }' +
            '.bold { font-weight: 800; }' +
            '.dash { border: none; border-top: 1px dashed #000; margin: 6px 0; }' +
            '.row { display: flex; justify-content: space-between; gap: 6px; margin: 3px 0; font-size: 10px; }' +
            '</style></head><body>' +
            `<h1>${esc(corte.titulo || 'CORTE DE TURNO')}</h1>` +
            (corte.fecha_operacional
                ? `<div class="meta" style="font-weight:700">Día operacional: ${esc(
                      new Date(corte.fecha_operacional + 'T12:00:00').toLocaleDateString('es-CL')
                  )}</div>`
                : '') +
            `<div class="meta">Desde ${esc(fmtDt(corte.desde))}</div>` +
            `<div class="meta">Hasta ${esc(fmtDt(corte.hasta))}</div>` +
            `<div class="meta">Cajero: ${esc(corte.caja_nombre || '—')}</div>` +
            (corte.usuario_nombre ? `<div class="meta">Operador: ${esc(corte.usuario_nombre)}</div>` : '') +
            '<hr class="sep" />' +
            '<div class="sec">VENTA POR FORMA DE PAGO</div>' +
            '<table><tbody>' +
            formaRows +
            extraVueltosRows +
            `<tr class="bold"><td class="col-l">Total ventas reales (${corte.cantidad_ventas || 0} vtas)</td><td class="col-r">${formatCurrency(totalVentasReales)}</td></tr>` +
            '</tbody></table>' +
            '<hr class="sep" />' +
            '<div class="sec">RESUMEN DE CIERRE</div>' +
            `<div class="row"><span class="col-l">Total efectivo turno</span><span class="col-r">${formatCurrency(r.total_efectivo_turno)}</span></div>` +
            `<div class="row"><span class="col-l">Total tarjeta digitado</span><span class="col-r">${formatCurrency(r.total_tarjeta_digitado)}</span></div>` +
            `<div class="row"><span class="col-l">Ingresos a caja</span><span class="col-r">${formatCurrency(r.ingresos_caja)}</span></div>` +
            (r.ingresos_pago_cuenta > 0
                ? `<div class="row"><span class="col-l" style="padding-left:8px">Pagos cuenta (efectivo)</span><span class="col-r">${formatCurrency(r.ingresos_pago_cuenta)}</span></div>`
                : '') +
            (r.pagos_cuenta_tarjeta_externa > 0
                ? `<div class="row"><span class="col-l" style="padding-left:8px">Pagos cuenta (tarj. externa)*</span><span class="col-r">${formatCurrency(r.pagos_cuenta_tarjeta_externa)}</span></div>`
                : '') +
            (r.ingresos_inyeccion_efectivo > 0
                ? `<div class="row"><span class="col-l" style="padding-left:8px">Inyección efectivo</span><span class="col-r">${formatCurrency(r.ingresos_inyeccion_efectivo)}</span></div>`
                : '') +
            (r.egresos_retiro_seguridad > 0
                ? `<div class="row"><span class="col-l" style="padding-left:8px">Retiro seguridad</span><span class="col-r">-${formatCurrency(r.egresos_retiro_seguridad)}</span></div>`
                : '') +
            (Array.isArray(corte.movimientos_efectivo) && corte.movimientos_efectivo.length > 0
                ? '<div class="sec">DETALLE INYECCIÓN / RETIRO</div>' +
                  corte.movimientos_efectivo.map((m) => {
                      const sign = m.tipo === 'ingreso' ? '+' : '-';
                      const hora = formatFechaHoraChile(m.created_at);
                      const lineas = desgloseLineas(m.desglose);
                      const resp = m.responsable ? `<br><span style="font-size:9px">Responsable: ${esc(m.responsable)}</span>` : '';
                      const det = lineas.length
                          ? lineas
                              .map((l) => `<br><span style="font-size:9px">${l.qty} × ${esc(l.label)} = ${formatCurrency(l.subtotal)}</span>`)
                              .join('')
                          : '';
                      return (
                          `<div class="row"><span class="col-l">${esc(hora ? `${hora} ${m.etiqueta}` : m.etiqueta)}${resp}${det}` +
                          `</span><span class="col-r">${sign}${formatCurrency(m.monto)}</span></div>`
                      );
                  }).join('')
                : '') +
            '<div class="row"><span class="col-l">Egresos de caja</span><span class="col-r"></span></div>' +
            `<div class="row"><span class="col-l" style="padding-left:8px">Vuelto en efectivo</span><span class="col-r">${formatCurrency(r.egresos_vuelto_efectivo || 0)}</span></div>` +
            `<div class="row"><span class="col-l" style="padding-left:8px">Vuelto/giro con tarjeta</span><span class="col-r">${formatCurrency(r.egresos_vuelto_tarjeta || 0)}</span></div>` +
            `<div class="row"><span class="col-l" style="padding-left:8px">Total egresos</span><span class="col-r">${formatCurrency(r.egresos_caja)}</span></div>` +
            `<div class="row bold"><span class="col-l">Total caja</span><span class="col-r">${formatCurrency(r.total_caja)}</span></div>` +
            `<div class="row"><span class="col-l">Monto base caja</span><span class="col-r">${formatCurrency(r.monto_base_caja)}</span></div>` +
            contadoLine +
            forzadoBlock +
            '<div class="dash"></div>' +
            `<div class="row bold" style="font-size:12px"><span class="col-l">Total caja + base</span><span class="col-r">${formatCurrency(r.total_caja_mas_base)}</span></div>` +
            '<div class="dash"></div>' +
            '<div class="sec">VENTAS POR CATEGORÍA</div>' +
            `<table><tbody>${catRows}` +
            (Math.abs(envaseNetoBoleta) >= 1
                ? `<tr><td class="col-l" style="padding-left:8px; font-style:italic;">Envases en boleta (neto)</td><td class="col-r">${formatCurrency(envaseNetoBoleta)}</td></tr>`
                : '') +
            (Math.abs(redondeoBoleta) >= 1
                ? `<tr><td class="col-l" style="padding-left:8px; font-style:italic;">Redondeo boleta ($10)</td><td class="col-r">${formatCurrency(redondeoBoleta)}</td></tr>`
                : '') +
            `<tr class="bold"><td class="col-l">Suma cat. + ajustes</td><td class="col-r">${formatCurrency(totalVentasReales)}</td></tr>` +
            '</tbody></table>' +
            '<script>window.onload=function(){window.print();setTimeout(function(){window.close();},500);}</script>' +
            '</body></html>';
        printWindow.document.write(html);
        printWindow.document.close();
    };

    /** Imprime corte de turno con datos al momento (no cierra la sesión). */
    const handleImprimirCorteTurno = async () => {
        try {
            const res = await fetch(`${API_URL}/api/cajas/cierre-resumen`, { headers: authHeader() });
            if (res.status === 401) {
                logout();
                return;
            }
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                showToast(err.error || 'No se pudo obtener el resumen para imprimir.', 'error');
                return;
            }
            const data = await res.json();
            if (data.corte_turno) {
                printCorteTurno(data.corte_turno);
            } else {
                showToast('No hay datos de corte para imprimir.', 'error');
            }
        } catch (e) {
            console.error(e);
            showToast('Error al preparar la impresión.', 'error');
        }
    };

    const productThumbSrc = (p) => productImageSrc(p?.foto_url);

    const addToCart = async (product, clearSearch = true) => {
        if (product.venta_bloqueada_precio || product.precio_bloqueado) {
            showToast('Producto bloqueado: requiere autorización de actualización de precio.', 'error');
            return;
        }
        // Productos por peso: mostrar modal para ingresar gramos
        if (product.venta_por_peso) {
            setPesoProductToAdd(product);
            setPesoGramsInput('');
            setShowPesoModal(true);
            return;
        }

        const unitsInCart = countProductUnits(cart, product.id);
        const newQuantity = unitsInCart + 1;
        const stockActual = product.stock_actual || 0;

        // Si el sistema marca 0 (p. ej. inventario mal cargado por sucursal), igual se puede vender mercadería física en caja/mostrador
        if (stockActual > 0 && newQuantity > stockActual) {
            showToast(`Stock insuficiente según sistema. Disponible: ${stockActual} unidades`, 'error');
            return;
        }

        setCart(prev => {
            const existing = prev.find(item => item.id === product.id && !item.venta_por_peso && !item.isPromo);
            const next = existing
                ? prev.map(item => item.id === product.id && !item.venta_por_peso && !item.isPromo ? { ...item, quantity: item.quantity + 1 } : item)
                : [...prev, { ...product, quantity: 1, price: parseFloat(product.valor_producto) }];
            return commitCart(next);
        });
        // Clear search and refocus for faster scanning workflow
        if (clearSearch) {
            setSearchTerm('');
            setProducts([]);
            setTimeout(() => searchInputRef.current?.focus(), 50);
        }
    };

    const confirmAddPesoToCart = () => {
        if (!pesoProductToAdd) return;
        if (pesoProductToAdd.venta_bloqueada_precio || pesoProductToAdd.precio_bloqueado) {
            showToast('Producto bloqueado: requiere autorización de actualización de precio.', 'error');
            return;
        }
        const grams = parseInt(pesoGramsInput, 10) || 0;
        if (grams <= 0) {
            showToast('Ingrese una cantidad válida en gramos', 'error');
            return;
        }
        const precioPorKilo = parseFloat(pesoProductToAdd.precio_por_kilo || pesoProductToAdd.valor_producto) || 0;
        const stockActual = pesoProductToAdd.stock_actual || 0;
        if (stockActual > 0 && grams > stockActual) {
            showToast(`Stock insuficiente. Disponible: ${stockActual} g`, 'error');
            return;
        }
        const price = Math.round((precioPorKilo / 1000) * grams);
        const cartItem = {
            ...pesoProductToAdd,
            id: pesoProductToAdd.id,
            quantity: 1,
            grams,
            price,
            venta_por_peso: true,
            cartItemId: `peso-${pesoProductToAdd.id}-${Date.now()}`
        };
        setCart(prev => [...prev, cartItem]);
        setShowPesoModal(false);
        setPesoProductToAdd(null);
        setPesoGramsInput('');
        setSearchTerm('');
        setProducts([]);
        setTimeout(() => searchInputRef.current?.focus(), 50);
    };

    const getCartItemKey = (item) => item.cartItemId || item.id;
    const updateQuantity = (cartItemKey, change) => {
        setCart(prev => {
            const item = prev.find(i => getCartItemKey(i) === cartItemKey);
            if (!item) return prev;
            if (item.venta_por_peso) {
                if (change < 0) return prev.filter(i => getCartItemKey(i) !== cartItemKey);
                return prev;
            }
            if (item.autoApplied || (!item.isPromo && !item.venta_por_peso)) {
                const productId = item.autoApplied ? item.auto_product_id : item.id;
                const snapshot = item.auto_product_snapshot || snapshotProduct(item);
                const nextTotal = countProductUnits(prev, productId) + change;
                if (nextTotal < 1) {
                    return commitCart(setProductUnits(prev, productId, 0, snapshot));
                }
                const stockActual = snapshot?.stock_actual || item.stock_actual || 0;
                if (change > 0 && stockActual > 0 && nextTotal > stockActual) {
                    showToast(`Stock insuficiente. Disponible: ${stockActual} unidades`, 'error');
                    return prev;
                }
                return commitCart(setProductUnits(prev, productId, nextTotal, snapshot));
            }
            const newQuantity = item.quantity + change;
            if (newQuantity < 1) return prev.filter(i => getCartItemKey(i) !== cartItemKey);
            return prev.map(i => getCartItemKey(i) === cartItemKey ? { ...i, quantity: newQuantity } : i);
        });
    };

    const removeFromCart = (cartItemId) => setCart(prev => {
        const next = prev.filter(item => (item.cartItemId || item.id) !== cartItemId);
        return commitCart(next);
    });

    const total = calculateTotal();
    const { payments: paymentsNumericForChequeo, giroAdded: giroAddedChequeo, cashChange: cashChangeChequeo } = getPaymentsForCharge(total);
    const vueltoManualChequeoPago = getGiroIngresado();
    const valeLimitsChequeo = getCardValeLimits(total);
    const giroExcedeMaximo = cashChangeChequeo <= 0 && vueltoManualChequeoPago > GIRO_TARJETA_MAX_CLP;
    const giroSinTarjeta = vueltoManualChequeoPago > 0
        && paymentsNumericForChequeo.tarjeta_debito + paymentsNumericForChequeo.tarjeta_credito <= 0
        && cashChangeChequeo <= 0;
    const vueltoAEntregar = cashChangeChequeo > 0
        ? cashChangeChequeo
        : (cajeroPuedeEjecutarCobro && vueltoManualChequeoPago > 0
            ? vueltoManualChequeoPago
            : Math.abs(getTotalPaid() - total));
    const valeExcedeMaximo = valeLimitsChequeo.cardExceeds;
    const chequeoVueltoEnPago =
        showPaymentModal && requiereCajaOperativa && cajaSession?.isOpen
            ? computeEfectivoFaltanteParaVuelto(
                cajaSession.efectivo_disponible_en_caja,
                total,
                paymentsNumericForChequeo,
                vueltoManualChequeoPago
            )
            : null;
    const faltaEfectivoParaVuelto = chequeoVueltoEnPago?.falta > 0;
    const pagoBloqueado = getTotalPaid() < total
        || faltaEfectivoParaVuelto
        || giroExcedeMaximo
        || giroSinTarjeta
        || valeExcedeMaximo;

    // Determinar si el cajero debe abrir caja
    // Solo mostrar la pantalla si:
    // 1. Es cajero
    // 2. El estado ya se cargó (no está cargando)
    // 3. No tiene caja abierta (cajaSession es null O cajaSession.isOpen es false)
    // Pero NO mostrar si cajaSession es null y todavía está cargando
    const cajeroMustOpenCaja = requiereCajaOperativa &&
                                !cajaStatusLoading && 
                                cajaSession !== null && 
                                !cajaSession?.isOpen;
    
    // Debug: Log para entender cuándo se muestra la pantalla
    useEffect(() => {
        if (isCajero) {
            console.log('🔍 Evaluando cajeroMustOpenCaja:', {
                isCajero,
                cajaStatusLoading,
                cajaSession,
                cajaSessionIsOpen: cajaSession?.isOpen,
                cajeroMustOpenCaja,
                willShowScreen: cajeroMustOpenCaja && !cajaStatusLoading
            });
        }
    }, [isCajero, cajaStatusLoading, cajaSession, cajeroMustOpenCaja]);

    // Debug: Log del estado completo para depuración
    useEffect(() => {
        if (canCompleteSale) {
            const userRole = user?.role;
            const isAdmin = userRole === ROLE_ADMIN;
            const buttonDisabled =
                cart.length === 0 ||
                loading ||
                (requiereCajaOperativa && !puedeEjecutarCobro) ||
                (isVendedorRole && !activePreventaId);
            console.log('🔍 Estado completo POS:', JSON.stringify({
                userRole,
                isCajero,
                isAdmin,
                isPreventaPrevendedor,
                isVendedorRole,
                canCompleteSale,
                cajaSession: cajaSession ? { isOpen: cajaSession.isOpen, hasSession: !!cajaSession.session } : null,
                cajaSessionIsOpen: cajaSession?.isOpen,
                cajeroMustOpenCaja,
                cajaStatusLoading,
                availableCajas: availableCajas.length,
                cartLength: cart.length,
                loading,
                activePreventaId,
                buttonDisabled,
                buttonDisabledReasons: {
                    cartEmpty: cart.length === 0,
                    loading: loading,
                    cajeroNoCaja: requiereCajaOperativa && !puedeEjecutarCobro,
                    vendedorNoPreventa: isVendedorRole && !activePreventaId
                }
            }, null, 2));
        }
    }, [canCompleteSale, isCajero, isPreventaPrevendedor, isVendedorRole, cajaSession, availableCajas.length, cajaStatusLoading, cart.length, loading, activePreventaId, user, cajeroMustOpenCaja]);

    const scanFocusPaused =
        isPrinting ||
        cajeroMustOpenCaja ||
        showPaymentModal ||
        showPesoModal ||
        showOpenCajaModal ||
        showCloseCajaModal ||
        showPagoCuentaModal ||
        showMovimientoEfectivoModal ||
        showVendedorPreventaModal ||
        showPreventaModal ||
        showMovimientosListModal;

    scanFocusPausedRef.current = scanFocusPaused;

    useEffect(() => {
        if (scanFocusPaused) return undefined;
        const t = setTimeout(restoreScanFocus, 40);
        return () => clearTimeout(t);
    }, [scanFocusPaused]);

    // Mostrar pantalla de "abrir caja" para cajeros que no tienen caja abierta
    // Solo mostrar cuando el estado ya se cargó y no está cargando
    if (cajeroMustOpenCaja) {
        console.log('🖥️ Mostrando pantalla de abrir caja:', {
            isCajero,
            cajaStatusLoading,
            cajaSession,
            cajaSessionIsOpen: cajaSession?.isOpen,
            cajeroMustOpenCaja,
            availableCajas: availableCajas.length
        });
        return (
            <div className="pos-container relative min-h-screen flex items-center justify-center bg-gray-100">
                {availableCajas.length === 0 ? (
                    <div className="bg-white p-8 rounded-2xl shadow-lg max-w-md text-center">
                        <div className="bg-amber-50 p-4 rounded-full inline-flex mb-4">
                            <Lock size={48} className="text-amber-600" />
                        </div>
                        <h2 className="text-xl font-bold text-gray-800 mb-2">Debe abrir caja para operar</h2>
                        <p className="text-gray-600 mb-6">
                            No hay cajas asignadas a su usuario o no existen sucursales/cajas creadas. Contacte al administrador para que cree sucursales, cajas y le asigne una caja.
                        </p>
                        <a href="/sucursales" className="text-indigo-600 font-medium hover:underline">Ir a Sucursales</a>
                    </div>
                ) : (
                    <div className="fixed inset-0 bg-gray-900/90 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-gray-100 max-h-[90vh] flex flex-col">
                            <div className="flex flex-col items-center px-6 pt-6 pb-2 shrink-0">
                                <div className="bg-indigo-50 p-4 rounded-full mb-3">
                                    <Unlock size={32} className="text-indigo-600" />
                                </div>
                                <h2 className="text-xl font-bold text-gray-800">Abrir Caja</h2>
                                <p className="text-gray-500 text-center mt-2 text-sm">Para continuar debe abrir caja e ingresar el monto y la cantidad de billetes/monedas.</p>
                            </div>
                            <form onSubmit={handleOpenCaja} className="flex flex-col min-h-0 flex-1">
                                <div className="flex flex-col gap-4 overflow-y-auto px-6 py-3 flex-1 min-h-0">
                                <div className="form-group">
                                    <label className="text-gray-700 font-semibold mb-1">Seleccionar Caja</label>
                                    <select
                                        required
                                        className="w-full border-gray-300 rounded-lg p-3 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 transition-all outline-none"
                                        value={openCajaData.caja_id}
                                        onChange={e => setOpenCajaData({ ...openCajaData, caja_id: e.target.value })}
                                    >
                                        <option value="">Seleccione una caja...</option>
                                        {availableCajas.map(c => (
                                            <option key={c.id} value={c.id}>{c.sucursal_nombre ? `${c.nombre} (${c.sucursal_nombre})` : c.nombre}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="text-gray-700 font-semibold mb-1">Monto en efectivo en la caja registradora</label>
                                    <div className="relative">
                                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">$</span>
                                        <input
                                            required
                                            type="number"
                                            min="0"
                                            className="w-full border-gray-300 rounded-lg p-3 pl-10 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 transition-all outline-none text-lg font-bold text-gray-800"
                                            placeholder="0"
                                            value={openCajaData.monto_inicial}
                                            onChange={e => setOpenCajaData({ ...openCajaData, monto_inicial: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <DenominacionEfectivo
                                    value={openCajaDesglose}
                                    onChange={(next, total) => {
                                        setOpenCajaDesglose(next);
                                        setOpenCajaData((prev) => ({ ...prev, monto_inicial: String(total) }));
                                    }}
                                    label="Cantidad de billetes y monedas"
                                    accentColor="#4f46e5"
                                    compact
                                    showTotal={false}
                                />
                                </div>
                                <div className="px-6 py-4 border-t bg-white shrink-0">
                                <button type="submit" className="w-full bg-indigo-500 text-gray-900 p-4 rounded-xl font-bold hover:bg-indigo-700 shadow-lg min-h-[44px]">
                                    Abrir Sesión
                                </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <>
        <div className="pos-wrapper flex flex-col w-full h-full min-h-0">
            {/* Barra superior: caja abierta + botón cerrar */}
            {canCompleteSale && cajaSession?.isOpen && (
                <div className="flex items-center justify-between px-4 py-2.5 bg-green-50 border-b border-green-200 text-green-800 shrink-0">
                    <div className="flex items-center gap-2">
                        <Unlock size={18} className="text-green-600" />
                        <span className="font-semibold">Caja abierta:</span>
                        <span className="font-bold">
                            {cajaSession?.session?.caja_nombre || 
                             availableCajas.find(c => c.id == cajaSession?.session?.caja_id)?.nombre || 
                             'POS'}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={handleImprimirCorteTurno}
                            className="flex items-center gap-2 px-3 py-1.5 bg-white/80 text-green-900 border border-green-300 rounded-lg hover:bg-white font-medium text-sm transition-colors cursor-pointer"
                            title="Imprimir corte de turno sin cerrar"
                        >
                            <Printer size={16} /> Imprimir corte
                        </button>
                        <button
                            type="button"
                            onClick={openCloseCajaModal}
                            className="flex items-center gap-2 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 font-medium text-sm transition-colors cursor-pointer"
                            title="Cerrar caja"
                        >
                            <Lock size={16} /> Cerrar Caja
                        </button>
                    </div>
                </div>
            )}
            {cajeroVenBloqueadoPorEstadoLocal && (
                <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 bg-amber-50 border-b border-amber-300 text-amber-950 shrink-0">
                    <span className="text-sm font-medium">
                        {cajaSession?.sales_blocked_reason ||
                            'Este turno está en una caja o sucursal no habilitada para ventas. Corríjalo antes de cobrar.'}
                    </span>
                    <Link
                        to="/sucursales"
                        className="text-sm font-semibold text-amber-900 underline hover:no-underline whitespace-nowrap"
                    >
                        Ir a Sucursales
                    </Link>
                </div>
            )}
        <div className="pos-container relative flex-1 min-h-0">
            {/* Toast notification - z-[9999] para que aparezca siempre encima de modales */}
            {toast.show && (
                <div className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-[9999] px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 animate-pulse ${
                    toast.type === 'error' 
                        ? 'bg-red-500 text-white' 
                        : toast.type === 'success' 
                            ? 'bg-green-500 text-white' 
                            : 'bg-yellow-500 text-white'
                }`}>
                    <span className="text-xl">⚠️</span>
                    <span className="font-medium">{toast.message}</span>
                </div>
            )}
            
            <div className="pos-left">
                <div className="search-bar relative">
                    <Search className="pos-search-icon" size={20} aria-hidden />
                    <input
                        ref={searchInputRef}
                        type="text"
                        placeholder={
                            isPrinting
                                ? 'Imprimiendo ticket… no escanee hasta que termine'
                                : isPreventaPrevendedor
                                ? 'Escanear código de barras, VND, o nombre (solo productos sin código de barras)...'
                                : isCajero && cajeroPuedeEjecutarCobro
                                    ? canScanVendedorBarcode
                                        ? 'Escanear código de barras, PRV, VND, ENV… o nombre (solo sin código de barras)...'
                                        : 'Escanear código de barras, PRV, ENV… o nombre (solo sin código de barras)...'
                                    : canScanVendedorBarcode
                                        ? 'Escanear código de barras, PRV, VND… o nombre (solo sin código de barras)...'
                                        : 'Escanear código de barras / PRV… o nombre (solo sin código de barras)...'
                        }
                        value={searchTerm}
                        onChange={(e) => {
                            if (isPrinting) return;
                            setSearchTerm(e.target.value);
                        }}
                        onKeyDown={handleScanFieldKeyDown}
                        onBlur={() => {
                            // After the click finishes, so +/- and other buttons still run.
                            setTimeout(restoreScanFocus, 120);
                        }}
                        autoFocus={!isPrinting}
                        disabled={isPrinting}
                        readOnly={isPrinting}
                        title={
                            isPreventaPrevendedor
                                ? 'Escanee el código de barras; el nombre solo encuentra productos sin código de barras.'
                                : 'Escanee el código de barras; el nombre solo encuentra productos sin código de barras. Caja puede escanear PRV y ENV.'
                        }
                    />
                    {/* Tooltip de ayuda para búsqueda */}
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 group">
                        <HelpCircle size={16} className="text-gray-400 hover:text-gray-600 cursor-help" />
                        <div className="absolute right-0 top-full mt-1 w-64 bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg z-50 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                            <div className="font-semibold mb-1">💡 Consejos de búsqueda:</div>
                            <ul className="list-disc list-inside space-y-1">
                                <li>Prioridad: escanee el código de barras del producto</li>
                                <li>El nombre solo lista productos sin código de barras en el sistema</li>
                                {!isPreventaPrevendedor && (
                                    <li>Escanee código de preventa (PRV00000123)</li>
                                )}
                                {canScanVendedorBarcode && (
                                    <li>
                                        {isCajero || isAdmin
                                            ? 'VND opcional (si no escanea, la venta se atribuye a usted)'
                                            : 'Escanee código VND o CAJ (ej. VND42 / CAJ15)'}
                                    </li>
                                )}
                                {isCajero && cajeroPuedeEjecutarCobro && (
                                    <li>Escanee código ENV del ticket para devolver dinero del envase</li>
                                )}
                                <li>Haga clic en un producto para agregarlo al carrito</li>
                            </ul>
                            <div className="absolute top-0 right-4 transform -translate-y-full">
                                <div className="border-4 border-transparent border-b-gray-900"></div>
                            </div>
                        </div>
                    </div>
                    {searchTerm && (
                        <button
                            onClick={() => setSearchTerm('')}
                            className="absolute right-10 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                            <X size={18} />
                        </button>
                    )}
                </div>
                <p className="text-xs text-gray-500 shrink-0 -mt-1 mb-0 px-0.5">
                    El cursor se queda aquí para escanear.
                    {canCompleteSaleWithLoadedPreventa
                        ? ' Con productos en la venta, Enter abre el método de pago.'
                        : ' Debajo: productos encontrados.'}
                </p>
                <div className="product-list">
                    {promotionsInSearch.map((promo) => {
                        const units = (promo.items || [])
                            .filter((item) => !item.extra)
                            .reduce((sum, item) => sum + (Number(item.cantidad) || 0), 0);
                        return (
                            <div
                                key={`promo-${promo.id}`}
                                className="pos-product-card pos-product-card--promo border-amber-200 bg-amber-50/30 cursor-pointer"
                                onClick={() => addPromoToCart(promo)}
                            >
                                <span className="text-xs font-bold text-amber-600 uppercase tracking-wide">Oferta</span>
                                <h3>{promo.nombre}</h3>
                                <p className="price text-amber-700">{formatCurrency(promo.monto)}</p>
                                <div className="stock-badge bg-amber-100 text-amber-800">
                                    {units > 1 ? `${units} un.` : 'Promoción'}
                                </div>
                            </div>
                        );
                    })}
                    {loading ? (
                        <div className="p-8 text-center text-gray-500">Buscando productos...</div>
                    ) : products.length === 0 && promotionsInSearch.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                            {searchTerm
                                ? 'No se encontraron productos'
                                : 'Escanee el código de barras, o escriba nombre solo para productos sin código de barras'}
                        </div>
                    ) : (
                        products.map(product => {
                                const thumb = productThumbSrc(product);
                                return (
                                <div
                                    key={product.id}
                                    className={`pos-product-card ${product.venta_bloqueada_precio || product.precio_bloqueado ? 'opacity-60' : ''}`}
                                    onClick={() => addToCart(product)}
                                >
                                    <div className="pos-product-thumb" aria-hidden>
                                        {thumb ? (
                                            <img src={thumb} alt="" loading="lazy" onError={retryProductImageOnce} />
                                        ) : (
                                            <span className="pos-product-thumb-placeholder">
                                                <Package size={22} strokeWidth={1.5} />
                                            </span>
                                        )}
                                    </div>
                                    <div className="pos-product-card-body">
                                        <h3>{product.nombre}</h3>
                                        {(product.venta_bloqueada_precio || product.precio_bloqueado) && (
                                            <p className="text-xs font-semibold text-red-600 mb-1">
                                                Producto bloqueado: requiere autorización de actualización de precio.
                                            </p>
                                        )}
                                        <p className="price">
                                            {product.venta_por_peso
                                                ? `${formatCurrency(product.precio_por_kilo || product.valor_producto)}/kg`
                                                : formatCurrency(product.valor_producto)}
                                        </p>
                                        <div className={`stock-badge ${
                                            (product.stock_actual ?? 0) <= 0
                                                ? 'bg-red-100 text-red-700'
                                                : product.stock_actual <= product.stock_minimo
                                                    ? 'bg-yellow-100 text-yellow-700'
                                                    : ''
                                        }`}>
                                            Stock: {product.venta_por_peso ? `${Math.max(0, product.stock_actual ?? 0)}g` : Math.max(0, product.stock_actual ?? 0)}
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            <div className="pos-right">
                <div className="pos-cart-toolbar flex flex-col border-b bg-gray-50 shrink-0">
                    <div className="flex items-center justify-between gap-2 py-2 px-3">
                        <h2 className="pos-cart-title flex items-center gap-2 m-0 border-none p-0 text-sm font-semibold text-gray-800 whitespace-nowrap">
                            <ShoppingCart size={16} /> Venta actual
                        </h2>
                        {/* Botón de ayuda sutil - solo hover */}
                        <div className="relative group shrink-0">
                            <button
                                type="button"
                                className="p-2 min-w-[36px] min-h-[36px] flex items-center justify-center text-gray-400 hover:text-blue-600 transition-colors rounded-full hover:bg-blue-50 cursor-pointer"
                                title="Ver ayuda"
                                aria-label="Ver ayuda"
                            >
                                <HelpCircle size={16} />
                            </button>
                            {/* Tooltip de ayuda solo al hacer hover */}
                            <div className="absolute right-0 top-full mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-xl z-50 p-3 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                <div className="text-sm text-gray-700 space-y-2.5">
                                    <div className="font-semibold text-gray-900 mb-2">Guía rápida</div>
                                    <div>
                                        <div className="font-medium mb-0.5 text-xs">1️⃣ Agregar productos</div>
                                        <div className="text-xs text-gray-600">Escanee códigos o busque por nombre solo si el producto no tiene código de barras; haga clic para agregar al carrito. Si hay promo de N unidades (ej. 4 por \$1.000), se aplica sola al pistolear.</div>
                                    </div>
                                    {isCajero && (
                                        <div>
                                            <div className="font-medium mb-0.5 text-xs">2️⃣ Abrir caja</div>
                                            <div className="text-xs text-gray-600">Debe abrir una caja antes de completar ventas</div>
                                        </div>
                                    )}
                                    {isPreventaPrevendedor && (
                                        <div>
                                            <div className="font-medium mb-0.5 text-xs">2️⃣ Generar preventa</div>
                                            <div className="text-xs text-gray-600">Cuando el cliente termine, use Generar Preventa para imprimir el ticket con código PRV para caja.</div>
                                        </div>
                                    )}
                                    {isVendedorRole && (
                                        <div>
                                            <div className="font-medium mb-0.5 text-xs">2️⃣ Cargar preventa</div>
                                            <div className="text-xs text-gray-600">La preventa la genera el perfil Preventa; usted cargue con &quot;Cargar Preventa&quot; o escanee PRV para cobrar.</div>
                                        </div>
                                    )}
                                    {(canCompleteSale || isVendedorRole) && (
                                        <div>
                                            <div className="font-medium mb-0.5 text-xs">3️⃣ Completar venta</div>
                                            <div className="text-xs text-gray-600">
                                                {isPreventaPrevendedor
                                                    ? '(Solo caja / admin)'
                                                    : isCajero || isAdmin
                                                        ? 'Venta directa: agregue productos y pulse Completar Venta (VND opcional). También puede cargar una preventa PRV.'
                                                        : 'Haga clic cuando el botón esté habilitado'}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="absolute top-0 right-4 transform -translate-y-full">
                                    <div className="border-4 border-transparent border-b-white"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Acciones de carrito / caja: wrapping evita solapar el título */}
                    <div className="flex flex-wrap gap-1.5 items-center px-3 pb-2 [&>*]:shrink-0">
                        {/* Indicador de estado de caja para cajeros - más compacto */}
                        {isCajero && (
                            <div className="relative group">
                                <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                                    cajaSession?.isOpen 
                                        ? 'bg-green-50 text-green-700 border border-green-200' 
                                        : 'bg-red-50 text-red-700 border border-red-200'
                                }`}>
                                    {cajaSession?.isOpen ? (
                                        <>
                                            <Unlock size={12} />
                                            <span className="hidden sm:inline">Abierta</span>
                                        </>
                                    ) : (
                                        <>
                                            <Lock size={12} />
                                            <span className="hidden sm:inline">Cerrada</span>
                                        </>
                                    )}
                                </div>
                                {/* Tooltip explicativo */}
                                {!cajeroPuedeEjecutarCobro && (
                                    <div className="absolute right-0 top-full mt-1 w-56 bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg z-50 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                        <div className="font-semibold mb-1">⚠️ Caja no disponible para cobro</div>
                                        <div>
                                            {cajeroVenBloqueadoPorEstadoLocal
                                                ? (cajaSession?.sales_blocked_reason || 'Active la sucursal y la caja en administración.')
                                                : 'Debe abrir una caja antes de poder completar ventas.'}
                                        </div>
                                        <div className="absolute top-0 right-4 transform -translate-y-full">
                                            <div className="border-4 border-transparent border-b-gray-900"></div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        {canLoadPreventaInPos && (
                            <button
                                onClick={() => { fetchPreventas(); setShowPreventaModal(true); }}
                                className="px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded text-xs font-medium hover:bg-indigo-100 transition-colors"
                                title="Cargar preventa pendiente para cobrar"
                            >
                                <span className="hidden sm:inline">Cargar </span>Preventa
                            </button>
                        )}
                    {canCompleteSale && cajaSession?.isOpen && (
                        <>
                            {cajeroPuedeEjecutarCobro && (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setMovimientoEfectivoTipo('inyeccion_efectivo');
                                            setMovimientoEfectivoMonto('');
                                            setMovimientoEfectivoDesglose(createEmptyDesglose());
                                            setMovimientoEfectivoSugerido(null);
                                            setShowMovimientoEfectivoModal(true);
                                        }}
                                        className="px-2 py-1.5 min-h-[36px] bg-emerald-50 text-emerald-800 rounded text-xs font-medium hover:bg-emerald-100 transition-colors flex items-center gap-1 cursor-pointer"
                                        title="Inyectar efectivo al cajón durante el turno"
                                    >
                                        <ArrowDownToLine size={14} />
                                        <span className="hidden md:inline">Inyectar</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setMovimientoEfectivoTipo('retiro_efectivo_seguridad');
                                            setMovimientoEfectivoMonto('');
                                            setMovimientoEfectivoDesglose(createEmptyDesglose());
                                            setMovimientoEfectivoSugerido(null);
                                            setShowMovimientoEfectivoModal(true);
                                        }}
                                        className="px-2 py-1.5 min-h-[36px] bg-amber-50 text-amber-900 rounded text-xs font-medium hover:bg-amber-100 transition-colors flex items-center gap-1 cursor-pointer"
                                        title="Retirar efectivo del cajón (seguridad / bóveda)"
                                    >
                                        <Shield size={14} />
                                        <span className="hidden md:inline">Retirar</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={openMovimientosListModal}
                                        className="px-2 py-1.5 min-h-[36px] bg-slate-50 text-slate-800 rounded text-xs font-medium hover:bg-slate-100 transition-colors flex items-center gap-1 cursor-pointer"
                                        title="Ver detalle de inyecciones y retiros de este turno"
                                    >
                                        <List size={14} />
                                        <span className="hidden md:inline">Movimientos</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => { setPagoCuentaMonto(''); setPagoCuentaModo('efectivo'); setShowPagoCuentaModal(true); }}
                                        className="px-2 py-1 bg-sky-50 text-sky-800 rounded text-xs font-medium hover:bg-sky-100 transition-colors"
                                        title="Registrar pago de cuenta/servicio (efectivo o tarjeta externa)"
                                    >
                                        <Wallet size={14} />
                                    </button>
                                </>
                            )}
                            <button
                                type="button"
                                onClick={handleImprimirCorteTurno}
                                className="px-2 py-1 bg-emerald-50 text-emerald-800 rounded text-xs font-medium hover:bg-emerald-100 transition-colors"
                                title="Imprimir corte de turno (sin cerrar)"
                            >
                                <Printer size={14} />
                            </button>
                            <button
                                onClick={openCloseCajaModal}
                                className="px-2 py-1 bg-red-50 text-red-700 rounded text-xs font-medium hover:bg-red-100 transition-colors"
                                title="Cerrar Caja"
                            >
                                <Lock size={14} />
                            </button>
                        </>
                    )}
                    {/* Botón para abrir caja cuando no tiene caja abierta */}
                    {canCompleteSale && !cajaSession?.isOpen && (
                        <button
                            onClick={() => setShowOpenCajaModal(true)}
                            className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors shadow-sm flex items-center gap-1.5"
                            title="Abrir Caja - requerido para completar ventas"
                        >
                            <Unlock size={14} />
                            <span className="hidden sm:inline">Abrir Caja</span>
                        </button>
                    )}
                    </div>
                </div>

                {activePreventaId && (
                    <div className="bg-green-50 px-4 py-2 border-b border-green-200 text-green-800 text-sm flex justify-between items-center">
                        <span className="flex items-center gap-2">
                            <FileText size={16} />
                            <span>
                                Preventa <strong>#{activePreventaId}</strong> cargada —{' '}
                                {canCompleteSale || isVendedorRole
                                    ? 'Ajuste cantidades con +/− si corresponde; luego complete la venta.'
                                    : 'Lista para revisión'}
                            </span>
                        </span>
                        <button 
                            onClick={() => { 
                                if (confirm('¿Desea descartar la preventa cargada?')) {
                                    setActivePreventaId(null); 
                                    setCart([]); 
                                }
                            }} 
                            className="text-green-600 hover:text-green-900"
                            title="Descartar preventa"
                        >
                            <X size={14} />
                        </button>
                    </div>
                )}

                <div className="cart-items">
                    {cart.length === 0 ? (
                        <div className="empty-cart-container">
                            <p className="empty-cart" style={{ writingMode: 'horizontal-tb', textOrientation: 'mixed' }}>El carrito está vacío</p>
                            <div className="relative inline-flex group">
                                <button 
                                    className="text-gray-400 hover:text-blue-600 transition-colors p-2 rounded-full hover:bg-blue-50"
                                    title="Ver ayuda"
                                >
                                    <Info size={18} />
                                </button>
                                {/* Tooltip de ayuda sutil al hacer hover */}
                                <div className="absolute left-1/2 top-full mt-2 transform -translate-x-1/2 w-72 bg-white border border-gray-200 rounded-lg shadow-xl z-50 p-4 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                    <div className="text-sm text-gray-700">
                                        <div className="font-semibold mb-2 text-gray-900">Pasos para completar una venta:</div>
                                        <div className="flex flex-col gap-1.5 text-xs">
                                            <div>1️⃣ <strong>Agregar productos:</strong> Busque productos arriba o haga clic en ellos</div>
                                            {isCajero && !cajaSession?.isOpen && (
                                                <div>2️⃣ <strong>Abrir caja:</strong> Debe abrir una caja antes de completar ventas</div>
                                            )}
                                            {isCajero && cajeroVenBloqueadoPorEstadoLocal && (
                                                <div>2️⃣ <strong>Sucursal o caja inactiva:</strong> Actívelos en Gestión de sucursales o en Cajas</div>
                                            )}
                                            {isCajero && cajeroPuedeEjecutarCobro && (
                                                <div>2️⃣ <strong>Caja lista:</strong> Puede registrar el cobro</div>
                                            )}
                                            <div>3️⃣ <strong>Completar venta:</strong> Con el campo de escaneo vacío, pulse Enter o el botón amarillo</div>
                                        </div>
                                    </div>
                                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-full">
                                        <div className="border-4 border-transparent border-b-white"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        cart.map((item, idx) => (
                            <div key={getCartItemKey(item) || idx} className={`cart-item${item.autoApplied ? ' cart-item--promo' : ''}`}>
                                <div className="item-info">
                                    <h4>{item.nombre}{item.venta_por_peso ? ` (${item.grams}g)` : ''}</h4>
                                    {item.autoApplied && (
                                        <p className="cart-item-promo-note">
                                            {item.auto_pack_qty} un. por {formatCurrency(item.price)}
                                            {item.quantity > 1 ? ` × ${item.quantity}` : ''}
                                        </p>
                                    )}
                                    <p>{formatCurrency(item.venta_por_peso ? item.price : item.price * item.quantity)}</p>
                                </div>
                                <div className="item-controls">
                                    <button onClick={() => updateQuantity(getCartItemKey(item), -1)}><Minus size={16} /></button>
                                    <span>{item.venta_por_peso ? `${item.grams}g` : item.quantity}</span>
                                    <button onClick={() => updateQuantity(getCartItemKey(item), 1)}><Plus size={16} /></button>
                                    <button onClick={() => removeFromCart(getCartItemKey(item))} className="remove-btn"><Trash2 size={16} /></button>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                <div className="pos-footer pos-footer--compact">
                    {showVendedorAssignment && (
                        <div className={`flex items-center justify-between gap-2 mb-2 px-2 py-1.5 rounded-lg text-sm ${
                            selectedVendedor
                                ? 'bg-indigo-50 dark:bg-indigo-900/30'
                                : saleRequiresVnd
                                    ? 'bg-amber-50 dark:bg-amber-900/30 border border-amber-200'
                                    : 'bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700'
                        }`}>
                            <span className={
                                selectedVendedor
                                    ? 'text-indigo-700 dark:text-indigo-300'
                                    : saleRequiresVnd
                                        ? 'text-amber-800 dark:text-amber-200'
                                        : 'text-gray-600 dark:text-gray-300'
                            }>
                                <strong>Vendedor:</strong>{' '}
                                {selectedVendedor
                                    ? selectedVendedor.name
                                    : saleRequiresVnd
                                        ? 'Escanee código VND o CAJ para cobrar'
                                        : 'Opcional — sin VND/CAJ se atribuye a usted'}
                            </span>
                            {selectedVendedor && (
                                <button
                                    type="button"
                                    onClick={() => setSelectedVendedor(null)}
                                    className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400"
                                    title="Quitar vendedor asignado"
                                >
                                    <X size={16} />
                                </button>
                            )}
                        </div>
                    )}
                    {ultimaVentaAnulable && (
                        <button
                            type="button"
                            disabled={loading}
                            onClick={() => anularVenta(ultimaVentaAnulable.id)}
                            className="w-full mb-2 px-3 py-2 rounded-lg text-sm font-semibold bg-red-50 text-red-800 border border-red-200 hover:bg-red-100 disabled:opacity-50"
                        >
                            Anular venta #{ultimaVentaAnulable.id} (cliente no pagó)
                        </button>
                    )}
                    <div className="total-section">
                        <span className="total-label">Total:</span>
                        <span className="total-amount">{formatCurrency(total)}</span>
                    </div>
                    {isPreventaPrevendedor && (
                        <div className="w-full mb-2 space-y-2">
                            <button
                                type="button"
                                disabled={loading}
                                onClick={() => printEnvasesTicket(ENVASE_TICKET_MONTO_CLP, 'ENVASE RETORNABLE')}
                                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold bg-amber-50 text-amber-900 border border-amber-200 hover:bg-amber-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title={`Imprime papel orientativo (${formatCurrency(ENVASE_TICKET_MONTO_CLP)}). El cobro lo registra caja al cobrar.`}
                            >
                                <Ticket size={18} strokeWidth={2.25} />
                                Ticket envase ({formatCurrency(ENVASE_TICKET_MONTO_CLP)})
                            </button>
                            <button
                                type="button"
                                disabled={loading}
                                onClick={() => printEnvasesTicket(ENVASE_BIDON_MONTO_CLP, 'ENVASE BIDÓN 20L')}
                                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold bg-sky-50 text-sky-900 border border-sky-200 hover:bg-sky-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title={`Ticket envase bidón agua 20 L (${formatCurrency(ENVASE_BIDON_MONTO_CLP)})`}
                            >
                                <Ticket size={18} strokeWidth={2.25} />
                                Ticket bidón 20L ({formatCurrency(ENVASE_BIDON_MONTO_CLP)})
                            </button>
                        </div>
                    )}
                    <div className="flex gap-2 w-full">
                        {/* Generar preventa: solo Preventa (VND obligatorio) y Admin. */}
                        {!activePreventaId && canCreatePreventa && (
                        <div className="flex-1 relative group">
                            <button
                                className="checkout-btn w-full bg-indigo-500 hover:bg-indigo-600"
                                disabled={cart.length === 0 || loading}
                                onClick={() => {
                                    if (cart.length === 0) return;
                                    if (isPreventaPrevendedor) {
                                        vendedorScanBufferRef.current = '';
                                        vendedorScanLastKeyRef.current = 0;
                                        setShowVendedorPreventaModal(true);
                                        setVendedorPreventaInput('');
                                        setVendedorPreventaResolved(null);
                                        setTimeout(() => vendedorPreventaInputRef.current?.focus(), 100);
                                    } else {
                                        // Cajero/Admin: preventa atribuida al usuario actual.
                                        doCreatePreventa(null);
                                    }
                                }}
                                title={cart.length === 0 ? 'Agregue productos al carrito primero' : 'Crear una preventa (venta pendiente)'}
                            >
                                <FileText size={20} /> Generar Preventa
                            </button>
                            {cart.length === 0 && (
                                <div className="absolute bottom-full left-0 mb-2 w-56 bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg z-50 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                    <div className="font-semibold mb-1">📝 Preventa</div>
                                    <div>Una preventa es una venta pendiente. El cliente puede pagar después en caja usando el código de barras del ticket.</div>
                                    <div className="absolute bottom-0 left-4 transform translate-y-full">
                                        <div className="border-4 border-transparent border-t-gray-900"></div>
                                    </div>
                                </div>
                            )}
                        </div>
                        )}

                        {/* Complete Sale Button - Cajero/Admin siempre, Vendedor solo si tiene preventa cargada */}
                        {canCompleteSaleWithLoadedPreventa && (
                            <div className="flex-1 flex flex-col gap-2">
                                <div className="relative group">
                                    <button
                                        className={`checkout-btn w-full ${
                                            (cart.length === 0 || loading || (requiereCajaOperativa && !puedeEjecutarCobro) || (isVendedorRole && !activePreventaId))
                                                ? 'disabled:bg-gray-400 disabled:cursor-not-allowed opacity-50'
                                                : ''
                                        }`}
                                        disabled={
                                            cart.length === 0 ||
                                            loading ||
                                            (requiereCajaOperativa && !puedeEjecutarCobro) ||
                                            (isVendedorRole && !activePreventaId) ||
                                            (saleRequiresVnd && !selectedVendedor)
                                        }
                                        onClick={() => {
                                            console.log('🛒 Intentando completar venta:', {
                                                cartLength: cart.length,
                                                loading,
                                                isCajero,
                                                cajaSessionIsOpen: cajaSession?.isOpen,
                                                cajaSession,
                                                isVendedorRole,
                                                activePreventaId
                                            });
                                            handleCompleteSale();
                                        }}
                                    >
                                        <DollarSign size={20} /> Completar Venta
                                        <span className="ml-1 rounded bg-black/10 px-1.5 py-0.5 text-[11px] font-bold tracking-wide">Enter</span>
                                    </button>
                                    
                                    {/* Tooltip sutil solo al hacer hover cuando está deshabilitado */}
                                    {(cart.length === 0 || (requiereCajaOperativa && !puedeEjecutarCobro) || (isVendedorRole && !activePreventaId) || (saleRequiresVnd && !selectedVendedor)) && (
                                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg z-50 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                            <div className="font-medium mb-1.5">¿Por qué está deshabilitado?</div>
                                            <div className="space-y-1.5">
                                                {cart.length === 0 && (
                                                    <div className="text-gray-300">• Agregue productos al carrito</div>
                                                )}
                                                {requiereCajaOperativa && !cajaSession?.isOpen && (
                                                    <div className="text-gray-300">• Haga clic en "Abrir Caja" para iniciar turno</div>
                                                )}
                                                {requiereCajaOperativa && cajeroVenBloqueadoPorEstadoLocal && (
                                                    <div className="text-gray-300">• Active la sucursal y marque la caja como operativa (Sucursales o Cajas)</div>
                                                )}
                                                {isVendedorRole && !activePreventaId && (
                                                    <div className="text-gray-300">• Debe cargar una preventa</div>
                                                )}
                                                {saleRequiresVnd && !selectedVendedor && (
                                                    <div className="text-gray-300">• Escanee código VND del vendedor</div>
                                                )}
                                            </div>
                                            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
                                                <div className="border-4 border-transparent border-t-gray-900"></div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                
                                {/* Mensaje sutil solo cuando es necesario y el botón está deshabilitado */}
                                {isCajero && !cajaSession?.isOpen && !cajeroVenBloqueadoPorEstadoLocal && cart.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => setShowOpenCajaModal(true)}
                                        className="w-full text-xs text-amber-700 bg-amber-100 border border-amber-300 rounded px-3 py-2 flex items-center justify-center gap-2 hover:bg-amber-200 transition-colors font-medium cursor-pointer"
                                    >
                                        <Unlock size={14} />
                                        <span>Haga clic para abrir caja y poder completar la venta</span>
                                    </button>
                                )}
                                {isCajero && cajeroVenBloqueadoPorEstadoLocal && cart.length > 0 && (
                                    <Link
                                        to="/sucursales"
                                        className="w-full text-xs text-red-900 bg-red-50 border border-red-200 rounded px-3 py-2 flex items-center justify-center gap-2 font-semibold hover:bg-red-100"
                                    >
                                        Corregir estado de sucursal / caja
                                    </Link>
                                )}
                                {isVendedorRole && !activePreventaId && cart.length > 0 && (
                                    <div className="w-full text-xs text-amber-600 bg-amber-50/50 border border-amber-200/50 rounded px-2 py-1.5 flex items-center gap-1.5">
                                        <FileText size={12} className="text-amber-600 flex-shrink-0" />
                                        <span>Debe cargar una preventa para completar la venta</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
        </div>
            
            {showOpenCajaModal && canCompleteSale && (
                <div className="fixed inset-0 bg-gray-900/90 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-gray-100 max-h-[90vh] flex flex-col">
                        <div className="flex flex-col items-center px-6 pt-6 pb-2 shrink-0">
                            <div className="bg-indigo-50 p-4 rounded-full mb-3">
                                <Unlock size={32} className="text-indigo-600" />
                            </div>
                            <h2 className="text-xl font-bold text-gray-800">Abrir Caja</h2>
                            <p className="text-gray-500 text-center mt-2 text-sm">Ingrese el monto y la cantidad de billetes/monedas que tiene la caja registradora al iniciar el turno.</p>
                        </div>

                        <form onSubmit={handleOpenCaja} className="flex flex-col min-h-0 flex-1">
                            <div className="flex flex-col gap-4 overflow-y-auto px-6 py-3 flex-1 min-h-0">
                            <div className="form-group">
                                <label className="text-gray-700 font-semibold mb-1">Seleccionar Caja</label>
                                <select
                                    required
                                    disabled={availableCajas.length === 0}
                                    className="w-full border-gray-300 rounded-lg p-3 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 transition-all outline-none disabled:opacity-60"
                                    value={openCajaData.caja_id}
                                    onChange={e => setOpenCajaData({ ...openCajaData, caja_id: e.target.value })}
                                >
                                    <option value="">Seleccione una caja...</option>
                                    {availableCajas.map(c => (
                                        <option key={c.id} value={c.id}>
                                            {c.sucursal_nombre ? `${c.nombre} (${c.sucursal_nombre})` : (c.nombre || `Caja #${c.id}`)}
                                        </option>
                                    ))}
                                </select>
                                {availableCajas.length === 0 && (
                                    <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2 mt-2">
                                        No hay cajas disponibles. Un administrador debe crear cajas en <strong>Cajas</strong> y asignarlas si corresponde. Cierre sesión y vuelva a entrar tras corregir permisos.
                                    </p>
                                )}
                            </div>
                            <div className="form-group">
                                <label className="text-gray-700 font-semibold mb-1">Monto en efectivo en la caja registradora</label>
                                <div className="relative">
                                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">$</span>
                                    <input
                                        required
                                        type="number"
                                        min="0"
                                        className="w-full border-gray-300 rounded-lg p-3 pl-10 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 transition-all outline-none text-lg font-bold text-gray-800"
                                        placeholder="0"
                                        value={openCajaData.monto_inicial}
                                        onChange={e => setOpenCajaData({ ...openCajaData, monto_inicial: e.target.value })}
                                    />
                                </div>
                                <p className="text-xs text-gray-500 mt-1">Conteo físico del dinero al iniciar el turno.</p>
                            </div>
                            <DenominacionEfectivo
                                value={openCajaDesglose}
                                onChange={(next, total) => {
                                    setOpenCajaDesglose(next);
                                    setOpenCajaData((prev) => ({ ...prev, monto_inicial: String(total) }));
                                }}
                                label="Cantidad de billetes y monedas"
                                accentColor="#4f46e5"
                                compact
                                showTotal={false}
                            />
                            </div>
                            <div className="px-6 py-4 border-t bg-white shrink-0">
                            <button type="submit" className="w-full bg-indigo-500 text-gray-900 p-4 rounded-xl font-bold hover:bg-indigo-700 shadow-lg min-h-[44px]">
                                Abrir Sesión
                            </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            {promoSelectionModal && (
                <div className="modal-overlay">
                    <div className="modal-content max-w-lg">
                        <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                            <h2 className="modal-title mb-0">Seleccione opción</h2>
                            <button
                                type="button"
                                onClick={() => setPromoSelectionModal(null)}
                                className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <p className="text-gray-600 text-sm mb-4">
                            {promoSelectionModal.promo.nombre} — elija la opción con stock:
                        </p>
                        {promoSelectionModal.allHaveStock && (
                            <button
                                type="button"
                                onClick={() => addPromoToCart(promoSelectionModal.promo, null, true)}
                                className="w-full mb-4 p-3 rounded-xl border-2 border-green-300 bg-green-50 text-green-800 font-semibold hover:bg-green-100 transition-colors"
                            >
                                Agregar pack completo
                            </button>
                        )}
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                            {promoSelectionModal.detail.items?.map((item) => {
                                const stock = parseInt(item.stock_actual, 10) || 0;
                                const cantidad = parseInt(item.cantidad, 10) || 1;
                                const hasStock = promoHasStock(item);
                                return (
                                    <button
                                        key={item.producto_id}
                                        type="button"
                                        disabled={!hasStock}
                                        onClick={() => addPromoToCart(promoSelectionModal.promo, { producto_id: item.producto_id, cantidad })}
                                        className={`w-full text-left p-4 rounded-xl border-2 transition-all flex justify-between items-center ${
                                            hasStock
                                                ? 'border-amber-200 bg-amber-50/50 hover:border-amber-400 hover:bg-amber-100 cursor-pointer'
                                                : 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-60'
                                        }`}
                                    >
                                        <div>
                                            <span className="font-medium text-gray-900">{item.nombre}</span>
                                            <span className="text-gray-500 text-sm ml-2">× {cantidad}</span>
                                        </div>
                                        <div className={`text-sm font-bold ${hasStock ? 'text-green-600' : 'text-red-600'}`}>
                                            Stock: {stock}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
            {showPesoModal && pesoProductToAdd && (
                <div className="fixed inset-0 bg-gray-900/90 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-sm border border-gray-100">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold text-gray-800">Ingresar gramos</h2>
                            <button onClick={() => { setShowPesoModal(false); setPesoProductToAdd(null); setPesoGramsInput(''); }} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>
                        <p className="text-gray-600 mb-4">{pesoProductToAdd.nombre}</p>
                        <p className="text-sm text-gray-500 mb-2">
                            Precio: {formatCurrency(pesoProductToAdd.precio_por_kilo || pesoProductToAdd.valor_producto)}/kg
                        </p>
                        <div className="grid grid-cols-4 gap-2 mb-4">
                            {[100, 250, 500, 1000].map(g => (
                                <button
                                    key={g}
                                    type="button"
                                    onClick={() => setPesoGramsInput(String(g))}
                                    className="flex-1 py-2 px-3 bg-gray-100 hover:bg-indigo-100 hover:text-indigo-700 rounded-lg text-sm font-medium transition-colors"
                                >
                                    {g}g
                                </button>
                            ))}
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Gramos</label>
                            <input
                                type="number"
                                min="1"
                                step="1"
                                value={pesoGramsInput}
                                onChange={e => setPesoGramsInput(e.target.value)}
                                placeholder="Ej: 350"
                                className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                autoFocus
                            />
                        </div>
                        {pesoGramsInput && parseInt(pesoGramsInput, 10) > 0 && (
                            <p className="text-sm text-emerald-600 font-medium mb-4">
                                Total: {formatCurrency(Math.round(((pesoProductToAdd.precio_por_kilo || pesoProductToAdd.valor_producto) / 1000) * parseInt(pesoGramsInput, 10)))}
                            </p>
                        )}
                        <button
                            type="button"
                            onClick={confirmAddPesoToCart}
                            className="w-full bg-indigo-500 text-gray-900 p-3 rounded-xl font-bold hover:bg-indigo-700"
                        >
                            Agregar al carrito
                        </button>
                    </div>
                </div>
            )}
            {showCloseCajaModal && ReactDOM.createPortal(
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 99999,
                        backdropFilter: 'blur(4px)'
                    }}
                >
                    <div
                        style={{
                            backgroundColor: '#ffffff',
                            borderRadius: '16px',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                            width: '90%',
                            maxWidth: '440px',
                            maxHeight: '90vh',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            fontFamily: 'system-ui, -apple-system, sans-serif'
                        }}
                    >
                        {/* Header - Explicit Column Layout */}
                        <div style={{
                            backgroundColor: '#fff1f2', // rose-50
                            padding: '24px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            borderBottom: '1px solid #ffe4e6'
                        }}>
                            <div style={{
                                backgroundColor: 'white',
                                padding: '12px',
                                borderRadius: '9999px',
                                marginBottom: '16px',
                                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                            }}>
                                <Lock size={32} color="#e11d48" /> {/* rose-600 */}
                            </div>
                            <h2 style={{
                                fontSize: '20px',
                                fontWeight: '700',
                                color: '#1f2937',
                                margin: '0 0 8px 0',
                                textAlign: 'center'
                            }}>
                                Cerrar Caja
                            </h2>
                            <p style={{
                                fontSize: '14px',
                                color: '#6b7280',
                                textAlign: 'center',
                                margin: 0,
                                lineHeight: '1.5'
                            }}>
                                Ingrese solo el efectivo contado en caja. Transferencias y tarjetas se toman del registro del sistema.
                            </p>
                        </div>

                        {/* Body - Cuadre por tipo de pago */}
                        <div style={{ padding: '24px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
                            {cierreResumen?.corte_turno?.fecha_operacional && (
                                <div style={{
                                    background: '#eef2ff',
                                    border: '1px solid #c7d2fe',
                                    borderRadius: '12px',
                                    padding: '10px 14px',
                                    marginBottom: '12px',
                                    fontSize: '13px',
                                    color: '#3730a3',
                                    fontWeight: 600,
                                }}>
                                    Día operacional:{' '}
                                    {new Date(
                                        cierreResumen.corte_turno.fecha_operacional + 'T12:00:00'
                                    ).toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                                </div>
                            )}
                            {cierreResumen && !ocultarEfectivoSistema && (
                                <div style={{
                                    background: '#f0fdf4',
                                    border: '1px solid #bbf7d0',
                                    borderRadius: '12px',
                                    padding: '12px 16px',
                                    marginBottom: '20px',
                                    fontSize: '13px',
                                    color: '#166534'
                                }}>
                                    <strong>Registro del sistema (transacciones):</strong>
                                    <div style={{ marginTop: '6px', display: 'grid', gap: '4px' }}>
                                        {cierreResumen.cuadre_efectivo && (
                                            <>
                                                <span>Monto inicial: {formatCurrency(cierreResumen.cuadre_efectivo.monto_inicial)}</span>
                                                <span>Efectivo ventas: {formatCurrency(cierreResumen.cuadre_efectivo.efectivo_ventas)}</span>
                                                {Number(cierreResumen.corte_turno?.resumen_cierre?.egresos_vuelto_efectivo) > 0 && (
                                                    <span>− Vuelto en efectivo: {formatCurrency(cierreResumen.corte_turno.resumen_cierre.egresos_vuelto_efectivo)}</span>
                                                )}
                                                {Number(cierreResumen.corte_turno?.resumen_cierre?.egresos_vuelto_tarjeta) > 0 && (
                                                    <span>− Vuelto/giro con tarjeta: {formatCurrency(cierreResumen.corte_turno.resumen_cierre.egresos_vuelto_tarjeta)}</span>
                                                )}
                                                {Number(cierreResumen.corte_turno?.resumen_cierre?.ingresos_inyeccion_efectivo) > 0 && (
                                                    <span>+ Inyección efectivo: {formatCurrency(cierreResumen.corte_turno.resumen_cierre.ingresos_inyeccion_efectivo)}</span>
                                                )}
                                                {Number(cierreResumen.corte_turno?.resumen_cierre?.egresos_retiro_seguridad) > 0 && (
                                                    <span>− Retiro seguridad: {formatCurrency(cierreResumen.corte_turno.resumen_cierre.egresos_retiro_seguridad)}</span>
                                                )}
                                                {Math.abs(Number(cierreResumen.cuadre_efectivo.ajustes_efectivo) || 0) > 0.001 && (
                                                    <span>Ajustes (envases u otros): {formatCurrency(cierreResumen.cuadre_efectivo.ajustes_efectivo)}</span>
                                                )}
                                            </>
                                        )}
                                        <span style={{ marginTop: '4px', fontWeight: 700 }}>Efectivo esperado en caja: {formatCurrency(cierreResumen.efectivo_esperado_en_caja)}</span>
                                    </div>
                                </div>
                            )}
                            {cierreResumen && ocultarEfectivoSistema && (
                                <div style={{
                                    background: '#f8fafc',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '12px',
                                    padding: '12px 16px',
                                    marginBottom: '20px',
                                    fontSize: '13px',
                                    color: '#475569',
                                    lineHeight: 1.45,
                                }}>
                                    Cuente el efectivo físico en el cajón e ingréselo abajo. El sistema no muestra el esperado al cajero.
                                </div>
                            )}
                            {cierreResumen && !ocultarEfectivoSistema && Number(cierreResumen.efectivo_esperado_en_caja) < 0 && (
                                <div style={{
                                    background: '#fffbeb',
                                    border: '1px solid #fcd34d',
                                    borderRadius: '12px',
                                    padding: '10px 14px',
                                    marginBottom: '16px',
                                    fontSize: '12px',
                                    color: '#92400e',
                                    lineHeight: 1.45,
                                }}>
                                    El sistema calcula un saldo negativo (vueltos/giros superan el efectivo). Ingrese el{' '}
                                    <strong>efectivo real</strong> en el cajón y use «Cerrar con descuadre» si no cuadra.
                                </div>
                            )}
                            {!ocultarEfectivoSistema && (
                            <button
                                type="button"
                                onClick={handleImprimirCorteTurno}
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    borderRadius: '12px',
                                    backgroundColor: '#ecfdf5',
                                    border: '1px solid #6ee7b7',
                                    color: '#047857',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    marginBottom: '16px',
                                }}
                            >
                                <Printer size={18} /> Imprimir corte de turno (sin cerrar)
                            </button>
                            )}
                            <form onSubmit={handleCloseCaja} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', textTransform: 'uppercase', color: '#374151', marginBottom: '6px' }}>Efectivo (conteo físico en caja)</label>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        autoComplete="off"
                                        placeholder="0"
                                        value={closeCajaData.efectivo_cierre}
                                        onChange={e => setCloseCajaData({ ...closeCajaData, efectivo_cierre: e.target.value.replace(/[^\d.,]/g, '') })}
                                        style={{ width: '100%', padding: '12px 16px', fontSize: '16px', border: '1px solid #d1d5db', borderRadius: '12px', boxSizing: 'border-box' }}
                                        autoFocus
                                    />
                                </div>
                                <DenominacionEfectivo
                                    value={closeCajaDesglose}
                                    onChange={(next, total) => {
                                        setCloseCajaDesglose(next);
                                        setCloseCajaData({ efectivo_cierre: String(total) });
                                    }}
                                    label="Cantidad de billetes y monedas"
                                    accentColor="#e11d48"
                                    compact
                                    showTotal={false}
                                />
                                {(() => {
                                    const contado = parseMontoClp(closeCajaData.efectivo_cierre);
                                    const esperado = Number(cierreResumen?.efectivo_esperado_en_caja);
                                    const diff =
                                        contado != null && Number.isFinite(esperado)
                                            ? contado - esperado
                                            : null;
                                    const hayDescuadre = diff != null && Math.abs(diff) > 1;
                                    return (
                                        <>
                                            {hayDescuadre && !ocultarEfectivoSistema && (
                                                <div style={{
                                                    fontSize: '13px',
                                                    color: '#b45309',
                                                    fontWeight: 600,
                                                    textAlign: 'center',
                                                }}>
                                                    Diferencia vs sistema: {formatCurrency(diff)}
                                                </div>
                                            )}
                                            {hayDescuadre && ocultarEfectivoSistema && (
                                                <div style={{
                                                    fontSize: '13px',
                                                    color: '#b45309',
                                                    fontWeight: 600,
                                                    textAlign: 'center',
                                                }}>
                                                    El conteo no cuadra con el sistema. Use «Cerrar con descuadre» si corresponde.
                                                </div>
                                            )}
                                            <label style={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: '10px',
                                                fontSize: '13px',
                                                color: '#374151',
                                                cursor: 'pointer',
                                                padding: '10px 12px',
                                                background: closeCajaForzar ? '#fff1f2' : '#f9fafb',
                                                border: `1px solid ${closeCajaForzar ? '#fda4af' : '#e5e7eb'}`,
                                                borderRadius: '12px',
                                            }}>
                                                <input
                                                    type="checkbox"
                                                    checked={closeCajaForzar}
                                                    onChange={e => setCloseCajaForzar(e.target.checked)}
                                                    style={{ marginTop: '2px' }}
                                                />
                                                <span>
                                                    <strong>Cerrar con descuadre</strong> — omitir validación de cuadre y registrar la diferencia en el corte.
                                                </span>
                                            </label>
                                        </>
                                    );
                                })()}

                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: '12px',
                                    position: 'sticky',
                                    bottom: 0,
                                    background: '#ffffff',
                                    paddingTop: '12px',
                                    zIndex: 2,
                                }}>
                                    <button
                                        type="button"
                                        onClick={() => setShowCloseCajaModal(false)}
                                        style={{
                                            padding: '12px',
                                            borderRadius: '12px',
                                            backgroundColor: 'white',
                                            border: '1px solid #d1d5db',
                                            color: '#374151',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            fontSize: '15px'
                                        }}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        style={{
                                            padding: '12px',
                                            borderRadius: '12px',
                                            backgroundColor: '#e11d48',
                                            border: 'none',
                                            color: 'white',
                                            fontWeight: '700',
                                            cursor: 'pointer',
                                            fontSize: '15px',
                                            boxShadow: '0 4px 6px -1px rgba(225, 29, 72, 0.3)'
                                        }}
                                    >
                                        Cerrar Turno
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {showPagoCuentaModal && ReactDOM.createPortal(
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 99999,
                        backdropFilter: 'blur(4px)',
                    }}
                    onClick={() => !pagoCuentaLoading && setShowPagoCuentaModal(false)}
                >
                    <div
                        style={{
                            backgroundColor: '#ffffff',
                            borderRadius: '16px',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                            width: '90%',
                            maxWidth: '400px',
                            padding: '24px',
                            fontFamily: 'system-ui, -apple-system, sans-serif',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#0c4a6e' }}>
                                Pago de cuenta / servicio
                            </h2>
                            <button
                                type="button"
                                onClick={() => setShowPagoCuentaModal(false)}
                                disabled={pagoCuentaLoading}
                                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#6b7280' }}
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
                            <button
                                type="button"
                                onClick={() => setPagoCuentaModo('efectivo')}
                                style={{
                                    flex: 1,
                                    padding: '8px 10px',
                                    borderRadius: '8px',
                                    border: pagoCuentaModo === 'efectivo' ? '2px solid #0284c7' : '1px solid #d1d5db',
                                    background: pagoCuentaModo === 'efectivo' ? '#e0f2fe' : '#fff',
                                    fontWeight: 600,
                                    fontSize: '0.8rem',
                                    cursor: 'pointer',
                                }}
                            >
                                Efectivo en caja
                            </button>
                            <button
                                type="button"
                                onClick={() => setPagoCuentaModo('tarjeta_externa')}
                                style={{
                                    flex: 1,
                                    padding: '8px 10px',
                                    borderRadius: '8px',
                                    border: pagoCuentaModo === 'tarjeta_externa' ? '2px solid #0284c7' : '1px solid #d1d5db',
                                    background: pagoCuentaModo === 'tarjeta_externa' ? '#e0f2fe' : '#fff',
                                    fontWeight: 600,
                                    fontSize: '0.8rem',
                                    cursor: 'pointer',
                                }}
                            >
                                Tarjeta directa
                            </button>
                        </div>
                        <p style={{ fontSize: '0.85rem', color: '#475569', margin: '0 0 16px', lineHeight: 1.45 }}>
                            {pagoCuentaModo === 'efectivo' ? (
                                <>
                                    El cliente paga una cuenta en <strong>efectivo</strong> en caja. Entra al arqueo físico pero{' '}
                                    <strong>no es venta de productos</strong>.
                                </>
                            ) : (
                                <>
                                    El cliente pagó con <strong>tarjeta directo a la entidad</strong> (Recauda, etc.). Solo registro
                                    informativo: <strong>no afecta</strong> caja física ni tarjeta digitada en el sistema.
                                </>
                            )}
                        </p>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                            {pagoCuentaModo === 'efectivo' ? 'Monto recibido en efectivo' : 'Monto pagado con tarjeta externa'}
                        </label>
                        <div style={{ position: 'relative', marginBottom: '16px' }}>
                            <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontWeight: 700 }}>$</span>
                            <input
                                type="number"
                                min="1"
                                value={pagoCuentaMonto}
                                onChange={(e) => setPagoCuentaMonto(e.target.value)}
                                placeholder="0"
                                autoFocus
                                style={{
                                    width: '100%',
                                    padding: '10px 10px 10px 28px',
                                    fontSize: '18px',
                                    fontWeight: 700,
                                    border: '1px solid #d1d5db',
                                    borderRadius: '10px',
                                    boxSizing: 'border-box',
                                }}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                type="button"
                                onClick={() => setShowPagoCuentaModal(false)}
                                disabled={pagoCuentaLoading}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    borderRadius: '10px',
                                    border: '1px solid #d1d5db',
                                    background: '#fff',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={handlePagoCuenta}
                                disabled={pagoCuentaLoading}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    borderRadius: '10px',
                                    border: 'none',
                                    background: '#0284c7',
                                    color: '#fff',
                                    fontWeight: 700,
                                    cursor: pagoCuentaLoading ? 'wait' : 'pointer',
                                }}
                            >
                                {pagoCuentaLoading ? 'Registrando…' : 'Registrar'}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {showMovimientoEfectivoModal && ReactDOM.createPortal(
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 99999,
                        backdropFilter: 'blur(4px)',
                        padding: '16px',
                        overflowY: 'auto',
                    }}
                    onClick={() => {
                        if (!movimientoEfectivoLoading) {
                            setShowMovimientoEfectivoModal(false);
                            setMovimientoEfectivoSugerido(null);
                        }
                    }}
                >
                    <div
                        style={{
                            backgroundColor: '#ffffff',
                            borderRadius: '16px',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                            width: '90%',
                            maxWidth: '440px',
                            maxHeight: '90vh',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            fontFamily: 'system-ui, -apple-system, sans-serif',
                            margin: 'auto',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ padding: '20px 24px 0', flexShrink: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: movimientoEfectivoTipo === 'inyeccion_efectivo' ? '#047857' : '#b45309' }}>
                                {movimientoEfectivoTipo === 'inyeccion_efectivo' ? 'Inyección de efectivo' : 'Retiro por seguridad'}
                            </h2>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowMovimientoEfectivoModal(false);
                                    setMovimientoEfectivoSugerido(null);
                                }}
                                disabled={movimientoEfectivoLoading}
                                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#6b7280' }}
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <p style={{ fontSize: '0.85rem', color: '#475569', margin: '0 0 12px', lineHeight: 1.45 }}>
                            {movimientoEfectivoTipo === 'inyeccion_efectivo' ? (
                                <>
                                    Agrega billetes al cajón durante el turno (fondo de cambio, reposición).{' '}
                                    <strong>No es venta</strong>; suma al efectivo esperado en el arqueo.
                                </>
                            ) : (
                                <>
                                    Retira efectivo del cajón hacia bóveda o caja fuerte por seguridad.{' '}
                                    <strong>Reduce</strong> el efectivo esperado en el arqueo; no es gasto del negocio.
                                </>
                            )}
                        </p>
                        {movimientoEfectivoSugerido != null && (
                            <p style={{
                                fontSize: '0.8rem',
                                color: '#047857',
                                background: '#ecfdf5',
                                border: '1px solid #a7f3d0',
                                borderRadius: 10,
                                padding: '8px 10px',
                                margin: '0 0 12px',
                            }}>
                                Sugerido para cubrir vuelto: <strong>{formatCurrency(movimientoEfectivoSugerido)}</strong>
                            </p>
                        )}
                        </div>
                        <div style={{ padding: '0 24px 8px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                            Monto en efectivo
                        </label>
                        <div style={{ position: 'relative', marginBottom: '12px' }}>
                            <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontWeight: 700 }}>$</span>
                            <input
                                type="number"
                                min="1"
                                value={movimientoEfectivoMonto}
                                onChange={(e) => setMovimientoEfectivoMonto(e.target.value)}
                                placeholder="0"
                                autoFocus
                                disabled={movimientoEfectivoLoading}
                                style={{
                                    width: '100%',
                                    padding: '10px 10px 10px 28px',
                                    fontSize: '18px',
                                    fontWeight: 700,
                                    border: '1px solid #d1d5db',
                                    borderRadius: '10px',
                                    boxSizing: 'border-box',
                                }}
                            />
                        </div>
                        <div style={{ marginBottom: '8px' }}>
                            <DenominacionEfectivo
                                value={movimientoEfectivoDesglose}
                                onChange={(next, total) => {
                                    setMovimientoEfectivoDesglose(next);
                                    setMovimientoEfectivoMonto(String(total));
                                }}
                                label="Cantidad de billetes y monedas"
                                accentColor={movimientoEfectivoTipo === 'inyeccion_efectivo' ? '#059669' : '#d97706'}
                                disabled={movimientoEfectivoLoading}
                                compact
                                showTotal={false}
                            />
                        </div>
                        </div>
                        <div style={{ display: 'flex', gap: '10px', padding: '12px 24px 20px', borderTop: '1px solid #e5e7eb', flexShrink: 0, background: '#fff' }}>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowMovimientoEfectivoModal(false);
                                    setMovimientoEfectivoSugerido(null);
                                }}
                                disabled={movimientoEfectivoLoading}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    minHeight: 44,
                                    borderRadius: '10px',
                                    border: '1px solid #d1d5db',
                                    background: '#fff',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={handleMovimientoEfectivo}
                                disabled={movimientoEfectivoLoading || (parseMontoClp(movimientoEfectivoMonto) ?? 0) <= 0}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    minHeight: 44,
                                    borderRadius: '10px',
                                    border: 'none',
                                    background: movimientoEfectivoTipo === 'inyeccion_efectivo' ? '#059669' : '#d97706',
                                    color: '#fff',
                                    fontWeight: 700,
                                    cursor: movimientoEfectivoLoading ? 'wait' : 'pointer',
                                    opacity: (parseMontoClp(movimientoEfectivoMonto) ?? 0) <= 0 ? 0.5 : 1,
                                }}
                            >
                                {movimientoEfectivoLoading ? 'Registrando…' : 'Registrar'}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {showMovimientosListModal && ReactDOM.createPortal(
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 99999,
                        backdropFilter: 'blur(4px)',
                        padding: '16px',
                    }}
                    onClick={() => setShowMovimientosListModal(false)}
                >
                    <div
                        style={{
                            backgroundColor: '#ffffff',
                            borderRadius: '16px',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                            width: '90%',
                            maxWidth: '480px',
                            maxHeight: '88vh',
                            overflowY: 'auto',
                            padding: '24px',
                            fontFamily: 'system-ui, -apple-system, sans-serif',
                            margin: 'auto',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#111827' }}>
                                Ingresos y egresos de efectivo
                            </h2>
                            <button
                                type="button"
                                onClick={() => setShowMovimientosListModal(false)}
                                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#6b7280' }}
                                aria-label="Cerrar"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 16px' }}>
                            Detalle de inyecciones (ingresos) y retiros por seguridad (egresos) de este turno. Hora de Chile.
                        </p>
                        {movimientosEfectivoLoading ? (
                            <p style={{ fontSize: 14, color: '#64748b', textAlign: 'center', padding: '24px 0' }}>Cargando…</p>
                        ) : movimientosEfectivo.length === 0 ? (
                            <p style={{ fontSize: 14, color: '#64748b', textAlign: 'center', padding: '24px 0' }}>
                                No hay inyecciones ni retiros registrados en este turno.
                            </p>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {movimientosEfectivo.map((m) => {
                                    const esIngreso = m.tipo === 'ingreso';
                                    const detalle = formatDesgloseDetalle(m.desglose);
                                    const hora = formatFechaHoraChile(m.created_at);
                                    return (
                                        <div
                                            key={m.id}
                                            style={{
                                                border: '1px solid #e5e7eb',
                                                borderRadius: 12,
                                                padding: '10px 12px',
                                                background: esIngreso ? '#f0fdf4' : '#fffbeb',
                                            }}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
                                                <span style={{ fontWeight: 700, fontSize: 13, color: esIngreso ? '#047857' : '#b45309' }}>
                                                    {esIngreso ? 'Ingreso' : 'Egreso'} · {m.etiqueta}
                                                </span>
                                                <span style={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: esIngreso ? '#047857' : '#b45309' }}>
                                                    {esIngreso ? '+' : '−'}{formatCurrency(m.monto)}
                                                </span>
                                            </div>
                                            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                                                {hora ? `${hora}` : ''}
                                                {m.responsable ? ` · Responsable: ${m.responsable}` : ''}
                                                {detalle ? ` · ${detalle}` : ' · sin desglose de billetes'}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => printComprobanteMovimientoEfectivo(m)}
                                                style={{
                                                    marginTop: 8,
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    border: '1px solid #cbd5e1',
                                                    background: '#fff',
                                                    borderRadius: 8,
                                                    padding: '6px 10px',
                                                    cursor: 'pointer',
                                                    minHeight: 36,
                                                }}
                                            >
                                                Imprimir
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>,
                document.body
            )}

            {/* Modal: Escanear vendedor/cajero antes de crear preventa (solo escáner) */}
            {showVendedorPreventaModal && (
                <div className="modal-overlay">
                    <div className="modal-content max-w-md">
                        <button onClick={() => setShowVendedorPreventaModal(false)} className="modal-close"><X size={20} /></button>
                        <h2 className="modal-title">Preventa — escanear código</h2>
                        <p className="text-gray-600 text-sm mb-4">
                            Escanee el código de barras del vendedor (VND…) o del cajero (CAJ…). No se puede escribir a mano.
                        </p>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Código escaneado</label>
                                <input
                                    ref={vendedorPreventaInputRef}
                                    type="text"
                                    value={vendedorPreventaInput}
                                    readOnly
                                    autoComplete="off"
                                    onKeyDown={(e) => {
                                        if (isPrinting) {
                                            e.preventDefault();
                                            return;
                                        }
                                        const now = Date.now();
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            const code = (vendedorScanBufferRef.current || vendedorPreventaInput).trim();
                                            vendedorScanBufferRef.current = '';
                                            if (code) setVendedorPreventaInput(code);
                                            if (vendedorPreventaResolved && !loading) {
                                                doCreatePreventa(vendedorPreventaResolved);
                                            }
                                            return;
                                        }
                                        if (e.key === 'Backspace' || e.key === 'Delete') {
                                            e.preventDefault();
                                            vendedorScanBufferRef.current = '';
                                            setVendedorPreventaInput('');
                                            setVendedorPreventaResolved(null);
                                            return;
                                        }
                                        if (e.key.length !== 1 || e.ctrlKey || e.metaKey || e.altKey) return;
                                        e.preventDefault();
                                        const gap = now - vendedorScanLastKeyRef.current;
                                        vendedorScanLastKeyRef.current = now;
                                        if (gap > SCANNER_KEY_GAP_MS && vendedorScanBufferRef.current.length > 0) {
                                            // Slow typing: reject and restart buffer with this char only if gap huge
                                            vendedorScanBufferRef.current = '';
                                            setVendedorPreventaInput('');
                                            showToast('Solo se acepta escaneo de código de barras (VND… / CAJ…).', 'error');
                                            return;
                                        }
                                        vendedorScanBufferRef.current += e.key;
                                        setVendedorPreventaInput(vendedorScanBufferRef.current);
                                        if (vendedorScanIdleTimerRef.current) clearTimeout(vendedorScanIdleTimerRef.current);
                                        vendedorScanIdleTimerRef.current = setTimeout(() => {
                                            // Incomplete scan left idle — clear so manual typing cannot linger
                                            if (!vendedorPreventaResolved) {
                                                vendedorScanBufferRef.current = '';
                                                setVendedorPreventaInput('');
                                            }
                                        }, 250);
                                    }}
                                    onPaste={(e) => e.preventDefault()}
                                    placeholder="Espere el escáner aquí…"
                                    className="w-full border border-gray-300 rounded-lg p-3 text-lg font-mono focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-slate-50"
                                    autoFocus
                                />
                                {vendedorPreventaResolved && (
                                    <p className="mt-2 text-sm text-green-600 font-medium">✓ {vendedorPreventaResolved.name}</p>
                                )}
                                {vendedorPreventaInput.trim() && !vendedorPreventaResolved && (
                                    <p className="mt-2 text-sm text-amber-600">Validando código…</p>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => vendedorPreventaResolved && doCreatePreventa(vendedorPreventaResolved)}
                                disabled={!vendedorPreventaResolved || loading}
                                className="w-full px-4 py-3 rounded-lg bg-indigo-500 text-gray-900 hover:bg-indigo-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {vendedorPreventaResolved ? `Crear preventa e imprimir (${vendedorPreventaResolved.name})` : 'Escanee VND… o CAJ… para continuar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Preventa List Modal */}
            {showPreventaModal && (
                <div className="modal-overlay">
                    <div className="modal-content max-w-2xl">
                        <button onClick={() => setShowPreventaModal(false)} className="modal-close"><X size={20} /></button>
                        <h2 className="modal-title">Preventas Pendientes</h2>
                        <div className="overflow-y-auto max-h-[400px]">
                            {preventas.length === 0 ? (
                                <p className="text-center py-8 text-gray-500">No hay preventas pendientes.</p>
                            ) : (
                                <table className="w-full text-left report-table">
                                    <thead>
                                        <tr>
                                            <SortableTh columnKey="id" {...preventaThProps}>ID</SortableTh>
                                            <SortableTh columnKey="vendedor_name" {...preventaThProps}>Vendedor</SortableTh>
                                            <SortableTh columnKey="cantidad_productos" {...preventaThProps}>Items</SortableTh>
                                            <SortableTh columnKey="sub_total" {...preventaThProps}>Total</SortableTh>
                                            <SortableTh columnKey="created_at" {...preventaThProps}>Hora</SortableTh>
                                            <th>Acción</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedPreventas.map(p => (
                                            <tr key={p.id}>
                                                <td>#{p.id}</td>
                                                <td>{p.vendedor_name || 'Unknown'}</td>
                                                <td>{p.cantidad_productos}</td>
                                                <td className="font-bold">{formatCurrency(p.sub_total)}</td>
                                                <td className="text-xs text-gray-500">{new Date(p.created_at).toLocaleTimeString()}</td>
                                                <td>
                                                    <button
                                                        onClick={() => loadPreventa(p.id)}
                                                        className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded hover:bg-indigo-100 text-sm"
                                                    >
                                                        Cargar
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showPaymentModal && ReactDOM.createPortal(
                <div
                    role="presentation"
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 999999,
                        backgroundColor: 'rgba(0, 0, 0, 0.55)',
                        backdropFilter: 'blur(4px)',
                        overflow: 'hidden',
                        padding: '4px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        boxSizing: 'border-box',
                    }}
                    onMouseDown={(e) => {
                        if (e.target === e.currentTarget) setShowPaymentModal(false);
                    }}
                >
                    <div
                        role="dialog"
                        aria-labelledby="payment-modal-title"
                        style={{
                        backgroundColor: 'white',
                        borderRadius: '16px',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                        width: '100%',
                        maxWidth: '560px',
                        maxHeight: 'calc(100dvh - 8px)',
                        overflow: 'hidden',
                        margin: 0,
                        flexShrink: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        fontFamily: 'system-ui, -apple-system, sans-serif'
                    }}>
                        {/* Header */}
                        <div style={{
                            backgroundColor: '#10b981',
                            padding: '8px 12px',
                            color: 'white',
                            textAlign: 'center',
                            flexShrink: 0,
                        }}>
                            <h2 id="payment-modal-title" style={{ fontSize: '13px', fontWeight: 'bold', margin: 0 }}>Total a pagar</h2>
                            <p style={{ fontSize: '22px', fontWeight: '800', margin: '0' }}>{formatCurrency(total)}</p>
                            {opcionesSesionCajaVentasActivas && (boletaCargoEnvaseMonto > 0 || boletaDescuentoDevolucionEnvase) && (
                                <div
                                    style={{
                                        marginTop: '6px',
                                        paddingTop: '6px',
                                        borderTop: '1px solid rgba(255,255,255,0.35)',
                                        fontSize: '11px',
                                        fontWeight: '600',
                                        textAlign: 'left',
                                        lineHeight: 1.35,
                                    }}
                                >
                                    <div style={{ opacity: 0.95 }}>Productos: {formatCurrency(calculateCartSubtotal())}</div>
                                    {boletaCargoEnvaseMonto > 0 && (
                                        <div style={{ opacity: 0.95 }}>
                                            + {boletaCargoEnvaseMonto === ENVASE_BIDON_MONTO_CLP ? 'Envase bidón 20L' : 'Ticket envases'}:{' '}
                                            {formatCurrency(boletaCargoEnvaseMonto)}
                                        </div>
                                    )}
                                    {boletaDescuentoDevolucionEnvase && (
                                        <div style={{ opacity: 0.95 }}>− Devolución envase: {formatCurrency(ENVASE_TICKET_MONTO_CLP)}</div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Body */}
                        <div style={{
                            padding: '6px 10px 6px',
                            boxSizing: 'border-box',
                            flex: 1,
                            minHeight: 0,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '6px',
                        }}>
                            {opcionesSesionCajaVentasActivas && (
                                <div
                                    className="w-full rounded-md border border-amber-200 bg-amber-50 px-1.5 py-1 shrink-0"
                                    style={{ boxSizing: 'border-box', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 8px' }}
                                >
                                    <label className="flex cursor-pointer items-center gap-1 text-[11px] font-semibold text-amber-950">
                                        <input
                                            type="checkbox"
                                            className="shrink-0 rounded border-amber-400 text-amber-700"
                                            checked={boletaCargoEnvaseMonto === ENVASE_TICKET_MONTO_CLP}
                                            onChange={(e) =>
                                                setBoletaCargoEnvaseMonto(e.target.checked ? ENVASE_TICKET_MONTO_CLP : 0)
                                            }
                                        />
                                        Envase {formatCurrency(ENVASE_TICKET_MONTO_CLP)}
                                    </label>
                                    <label className="flex cursor-pointer items-center gap-1 text-[11px] font-semibold text-sky-950">
                                        <input
                                            type="checkbox"
                                            className="shrink-0 rounded border-sky-400 text-sky-700"
                                            checked={boletaCargoEnvaseMonto === ENVASE_BIDON_MONTO_CLP}
                                            onChange={(e) =>
                                                setBoletaCargoEnvaseMonto(e.target.checked ? ENVASE_BIDON_MONTO_CLP : 0)
                                            }
                                        />
                                        Bidón {formatCurrency(ENVASE_BIDON_MONTO_CLP)}
                                    </label>
                                    <label className="flex cursor-pointer items-center gap-1 text-[11px] font-semibold text-rose-900">
                                        <input
                                            type="checkbox"
                                            className="shrink-0 rounded border-rose-300 text-rose-700"
                                            checked={boletaDescuentoDevolucionEnvase}
                                            onChange={(e) => setBoletaDescuentoDevolucionEnvase(e.target.checked)}
                                        />
                                        Dev. {formatCurrency(ENVASE_TICKET_MONTO_CLP)}
                                    </label>
                                    <button
                                        type="button"
                                        disabled={loading}
                                        onClick={() => printEnvasesTicket(ENVASE_TICKET_MONTO_CLP, 'ENVASE RETORNABLE')}
                                        className="ml-auto flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-950 border border-amber-300 disabled:opacity-50"
                                        title="Papel orientativo. No duplicar si el envase ya va en la boleta."
                                    >
                                        <Ticket size={12} strokeWidth={2.25} />
                                        Ticket
                                    </button>
                                    <button
                                        type="button"
                                        disabled={loading}
                                        onClick={() => printEnvasesTicket(ENVASE_BIDON_MONTO_CLP, 'ENVASE BIDÓN 20L')}
                                        className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-sky-100 text-sky-950 border border-sky-300 disabled:opacity-50"
                                        title="Ticket envase bidón agua 20 litros"
                                    >
                                        <Ticket size={12} strokeWidth={2.25} />
                                        Bidón
                                    </button>
                                </div>
                            )}
                            {requiereCajaOperativa &&
                                cajaSession?.isOpen &&
                                !ocultarEfectivoSistema &&
                                Number.isFinite(Number(cajaSession.efectivo_disponible_en_caja)) && (
                                <div style={{
                                    background: '#ecfdf5',
                                    border: '1px solid #6ee7b7',
                                    borderRadius: '6px',
                                    padding: '2px 8px',
                                    fontSize: '11px',
                                    color: '#047857',
                                    fontWeight: 600,
                                    textAlign: 'center',
                                    flexShrink: 0,
                                }}>
                                    Efectivo en caja: {formatCurrency(cajaSession.efectivo_disponible_en_caja)}
                                </div>
                            )}
                            {chequeoVueltoEnPago && chequeoVueltoEnPago.falta > 0 && (
                                    <div style={{
                                        background: '#fef2f2',
                                        border: '2px solid #fca5a5',
                                        borderRadius: '8px',
                                        padding: '8px',
                                        flexShrink: 0,
                                    }}>
                                        <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 700, color: '#b91c1c' }}>
                                            Efectivo insuficiente para el vuelto
                                        </p>
                                        <p style={{ margin: '0 0 10px', fontSize: '12px', color: '#7f1d1d', lineHeight: 1.45 }}>
                                            {ocultarEfectivoSistema ? (
                                                <>
                                                    No alcanza el efectivo del cajón para el vuelto de{' '}
                                                    {formatCurrency(chequeoVueltoEnPago.vueltoTotal)}. Faltan{' '}
                                                    {formatCurrency(chequeoVueltoEnPago.falta)}.
                                                </>
                                            ) : (
                                                <>
                                                    Disponible {formatCurrency(chequeoVueltoEnPago.efectivoDisponible)}
                                                    {chequeoVueltoEnPago.efectivoPagado > 0
                                                        ? ` + ${formatCurrency(chequeoVueltoEnPago.efectivoPagado)} recibido`
                                                        : ''}
                                                    {' '}no alcanza para vuelto de {formatCurrency(chequeoVueltoEnPago.vueltoTotal)}.
                                                    Faltan {formatCurrency(chequeoVueltoEnPago.falta)}.
                                                </>
                                            )}
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => abrirInyeccionDesdePago(chequeoVueltoEnPago.falta)}
                                            style={{
                                                width: '100%',
                                                padding: '10px',
                                                borderRadius: '8px',
                                                border: 'none',
                                                background: '#059669',
                                                color: 'white',
                                                fontWeight: 700,
                                                fontSize: '13px',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            Inyectar efectivo ({formatCurrency(chequeoVueltoEnPago.falta)})
                                        </button>
                                    </div>
                                )}

                            {/* Payment methods: tres columnas compactas cuando cabe */}
                            <div className="grid grid-cols-3 max-[440px]:grid-cols-1 gap-1.5 shrink-0">
                                {/* Efectivo */}
                                <div style={{ backgroundColor: '#f0fdf4', border: '2px solid #86efac', borderRadius: '8px', padding: '6px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '700', color: '#166534', marginBottom: '4px', fontSize: '11px' }}>
                                        <DollarSign size={14} /> Efectivo
                                    </label>
                                    <div style={{ position: 'relative' }}>
                                        <span style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontWeight: 'bold' }}>$</span>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            autoComplete="off"
                                            autoFocus
                                            value={paymentData.efectivo}
                                            onChange={(e) => setPaymentData({ ...paymentData, efectivo: soloDigitosMonto(e.target.value) })}
                                            placeholder="0"
                                            style={{
                                                width: '100%',
                                                padding: '6px 6px 6px 22px',
                                                fontSize: '16px',
                                                fontWeight: 'bold',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '8px',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setPaymentData({
                                            ...paymentData,
                                            efectivo: String(expectedBoletaForEfectivo(total)),
                                        })}
                                        style={{
                                            marginTop: '4px',
                                            width: '100%',
                                            padding: '2px',
                                            border: '1px solid #86efac',
                                            borderRadius: '6px',
                                            background: '#fff',
                                            color: '#166534',
                                            fontSize: '10px',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        Boleta
                                    </button>
                                </div>

                                {/* Tarjeta Débito */}
                                <div style={{ backgroundColor: '#eff6ff', border: valeExcedeMaximo ? '2px solid #ef4444' : '2px solid #93c5fd', borderRadius: '8px', padding: '6px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '700', color: '#1e40af', marginBottom: '4px', fontSize: '11px' }}>
                                        <div style={{ width: '16px', height: '10px', backgroundColor: '#1e40af', borderRadius: '2px' }}></div> T. Débito
                                    </label>
                                    <div style={{ position: 'relative' }}>
                                        <span style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontWeight: 'bold' }}>$</span>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            autoComplete="off"
                                            value={paymentData.tarjeta_debito}
                                            onChange={(e) => setPaymentData({ ...paymentData, tarjeta_debito: soloDigitosMonto(e.target.value) })}
                                            placeholder="vale"
                                            style={{
                                                width: '100%',
                                                padding: '6px 6px 6px 22px',
                                                fontSize: '16px',
                                                fontWeight: 'bold',
                                                border: valeExcedeMaximo ? '2px solid #ef4444' : '1px solid #d1d5db',
                                                borderRadius: '8px',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setPaymentData({
                                            ...paymentData,
                                            tarjeta_debito: String(expectedBoletaForCardField('tarjeta_debito', total)),
                                        })}
                                        style={{
                                            marginTop: '4px',
                                            width: '100%',
                                            padding: '2px',
                                            border: '1px solid #93c5fd',
                                            borderRadius: '6px',
                                            background: '#fff',
                                            color: '#1e40af',
                                            fontSize: '10px',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        Boleta
                                    </button>
                                </div>

                                {/* Tarjeta Crédito */}
                                <div style={{ backgroundColor: '#fdf4ff', border: valeExcedeMaximo ? '2px solid #ef4444' : '2px solid #e879f9', borderRadius: '8px', padding: '6px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '700', color: '#86198f', marginBottom: '4px', fontSize: '11px' }}>
                                        <div style={{ width: '16px', height: '10px', backgroundColor: '#86198f', borderRadius: '2px' }}></div> T. Crédito
                                    </label>
                                    <div style={{ position: 'relative' }}>
                                        <span style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontWeight: 'bold' }}>$</span>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            autoComplete="off"
                                            value={paymentData.tarjeta_credito}
                                            onChange={(e) => setPaymentData({ ...paymentData, tarjeta_credito: soloDigitosMonto(e.target.value) })}
                                            placeholder="vale"
                                            style={{
                                                width: '100%',
                                                padding: '6px 6px 6px 22px',
                                                fontSize: '16px',
                                                fontWeight: 'bold',
                                                border: valeExcedeMaximo ? '2px solid #ef4444' : '1px solid #d1d5db',
                                                borderRadius: '8px',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setPaymentData({
                                            ...paymentData,
                                            tarjeta_credito: String(expectedBoletaForCardField('tarjeta_credito', total)),
                                        })}
                                        style={{
                                            marginTop: '4px',
                                            width: '100%',
                                            padding: '2px',
                                            border: '1px solid #e879f9',
                                            borderRadius: '6px',
                                            background: '#fff',
                                            color: '#86198f',
                                            fontSize: '10px',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        Boleta
                                    </button>
                                </div>
                            </div>

                            <div style={{
                                backgroundColor: '#f0fdf4',
                                border: '2px solid #86efac',
                                borderRadius: '8px',
                                padding: '6px 8px',
                                flexShrink: 0,
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
                                    <label style={{ fontWeight: '700', color: '#166534', fontSize: '12px' }}>
                                        Billete recibido
                                    </label>
                                    {(parseMontoClp(paymentData.efectivo) || 0) > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => setPaymentData({ ...paymentData, efectivo: '' })}
                                            style={{
                                                border: 'none',
                                                background: 'transparent',
                                                color: '#166534',
                                                fontSize: '11px',
                                                fontWeight: 700,
                                                cursor: 'pointer',
                                                padding: 0,
                                            }}
                                        >
                                            Limpiar
                                        </button>
                                    )}
                                </div>
                                <div
                                    role="group"
                                    aria-label="Billete recibido"
                                    style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: '4px' }}
                                >
                                    {BILLETES_RECIBIDOS_CLP.map((amount) => (
                                        <button
                                            key={amount}
                                            type="button"
                                            onClick={() => sumarBilleteRecibido(amount)}
                                            style={{
                                                minHeight: '32px',
                                                borderRadius: '6px',
                                                border: '1px solid #16a34a',
                                                backgroundColor: '#fff',
                                                color: '#166534',
                                                fontSize: '12px',
                                                fontWeight: '700',
                                                cursor: 'pointer',
                                                padding: '4px 2px',
                                            }}
                                        >
                                            {formatCurrency(amount)}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {cajeroPuedeEjecutarCobro && (
                                <div style={{
                                    backgroundColor: '#fffbeb',
                                    border: '2px solid #fcd34d',
                                    borderRadius: '8px',
                                    padding: '6px 8px',
                                    flexShrink: 0,
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
                                        <label style={{ fontWeight: '700', color: '#92400e', fontSize: '12px' }}>
                                            Vuelto sugerido
                                        </label>
                                        <span style={{ fontSize: '10px', color: '#78350f' }}>
                                            máx. {formatCurrency(GIRO_TARJETA_MAX_CLP)}
                                        </span>
                                    </div>
                                    <div
                                        role="group"
                                        aria-label="Vuelto sugerido"
                                        style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '4px' }}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => setVueltoEfectivo('')}
                                            style={{
                                                minHeight: '32px',
                                                borderRadius: '6px',
                                                border: getGiroIngresado() === 0 ? '2px solid #d97706' : '1px solid #d97706',
                                                backgroundColor: getGiroIngresado() === 0 ? '#fde68a' : '#fff',
                                                color: '#92400e',
                                                fontSize: '11px',
                                                fontWeight: '700',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            Sin giro
                                        </button>
                                        {GIRO_TARJETA_QUICK_AMOUNTS.map((amount) => (
                                            <button
                                                key={amount}
                                                type="button"
                                                onClick={() => setVueltoEfectivo(
                                                    getGiroIngresado() === amount ? '' : String(amount)
                                                )}
                                                style={{
                                                    minHeight: '32px',
                                                    borderRadius: '6px',
                                                    border: getGiroIngresado() === amount ? '2px solid #d97706' : '1px solid #d97706',
                                                    backgroundColor: getGiroIngresado() === amount ? '#fde68a' : '#fff',
                                                    color: '#92400e',
                                                    fontSize: '11px',
                                                    fontWeight: '700',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                {formatCurrency(amount)}
                                            </button>
                                        ))}
                                    </div>
                                    {giroAddedChequeo > 0 && (
                                        <p style={{ fontSize: '11px', color: '#92400e', margin: '4px 0 0', fontWeight: 700 }}>
                                            Cobrar {formatCurrency(paymentsNumericForChequeo.tarjeta_debito + paymentsNumericForChequeo.tarjeta_credito)}
                                            {' · '}entregar {formatCurrency(vueltoManualChequeoPago)}
                                        </p>
                                    )}
                                    {cashChangeChequeo > 0 && (
                                        <p style={{ fontSize: '11px', color: '#92400e', margin: '4px 0 0', fontWeight: 700 }}>
                                            Efectivo {formatCurrency(paymentsNumericForChequeo.efectivo)}
                                            {' · '}vuelto {formatCurrency(cashChangeChequeo)}
                                        </p>
                                    )}
                                </div>
                            )}

                        </div>

                            {/* Ingresado + vuelto + confirmar: always visible */}
                            <div style={{
                                flexShrink: 0,
                                background: '#fff',
                                borderTop: '1px solid #e5e7eb',
                                padding: '6px 10px 10px',
                            }}>
                                <div style={{
                                    backgroundColor: valeExcedeMaximo || giroSinTarjeta ? '#fef2f2' : '#f3f4f6',
                                    padding: '6px 10px',
                                    borderRadius: '8px',
                                    marginBottom: '6px',
                                    border: valeExcedeMaximo || giroSinTarjeta ? '2px solid #fca5a5' : '1px solid #e5e7eb',
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                                        <span style={{ color: '#111827', fontWeight: 700 }}>Ingresado</span>
                                        <span style={{ fontWeight: '800', color: getTotalPaid() >= total && !valeExcedeMaximo ? '#047857' : '#b91c1c', fontSize: '15px' }}>
                                            {formatCurrency(getTotalPaid())}
                                        </span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '4px', marginTop: '4px', borderTop: '2px dashed #d1d5db' }}>
                                        <span style={{ fontWeight: '800', fontSize: '14px', color: '#111827' }}>
                                            {valeExcedeMaximo
                                                ? 'Vale mayor al permitido'
                                                : giroSinTarjeta
                                                    ? 'Falta tarjeta para el vuelto'
                                                    : getTotalPaid() >= total
                                                        ? 'Vuelto a entregar'
                                                        : 'Falta por cobrar'}
                                        </span>
                                        <span style={{
                                            fontWeight: '900',
                                            fontSize: '18px',
                                            color: valeExcedeMaximo || giroSinTarjeta || getTotalPaid() < total ? '#b91c1c' : '#047857'
                                        }}>
                                            {valeExcedeMaximo
                                                ? formatCurrency(valeLimitsChequeo.maxCard)
                                                : giroSinTarjeta
                                                    ? formatCurrency(valeLimitsChequeo.expectedVale)
                                                    : getTotalPaid() >= total
                                                        ? formatCurrency(vueltoAEntregar)
                                                        : formatCurrency(Math.abs(getTotalPaidTyped() - total))}
                                        </span>
                                    </div>
                                </div>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: '8px',
                                }}>
                                <button
                                    onClick={() => setShowPaymentModal(false)}
                                    style={{
                                        padding: '10px',
                                        borderRadius: '10px',
                                        border: '1px solid #d1d5db',
                                        backgroundColor: 'white',
                                        color: '#374151',
                                        fontSize: '15px',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={confirmSale}
                                    disabled={pagoBloqueado}
                                    style={{
                                        padding: '10px',
                                        borderRadius: '10px',
                                        border: 'none',
                                        backgroundColor: pagoBloqueado ? '#9ca3af' : '#10b981',
                                        color: 'white',
                                        fontSize: '15px',
                                        fontWeight: 'bold',
                                        cursor: pagoBloqueado ? 'not-allowed' : 'pointer',
                                        boxShadow: !pagoBloqueado ? '0 4px 6px -1px rgba(16, 185, 129, 0.4)' : 'none'
                                    }}
                                >
                                    Confirmar Pago
                                </button>
                                </div>
                            </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
}
